import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Mail, MailOpen, Plus, RefreshCw, Loader2, Trash2, AtSign, ShieldCheck, CircleDot, CheckCircle, XCircle, Inbox, X, UserCircle, Search, ExternalLink, AlertCircle, Link2, Unlink, MessageSquare, StickyNote, Users, Filter, Send, Reply, History, BarChart3, Settings, Edit3, Upload, Forward, Tag, Folder, FolderPlus, Bookmark, Database, FileText, Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReplyComposer from '../../components/bandeja/ReplyComposer';
import ComposeDialog from '../../components/bandeja/ComposeDialog';
import NotaMencionesInput from '../../components/bandeja/NotaMencionesInput';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLORS = ['#7c3aed', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#84cc16'];

export const ESTADOS = [
  { value: 'nuevo', label: 'Nuevo', color: 'bg-blue-100 text-blue-800' },
  { value: 'en_gestion', label: 'En gestión', color: 'bg-purple-100 text-purple-800' },
  { value: 'pendiente_externa', label: 'Pendiente externo', color: 'bg-amber-100 text-amber-800' },
  { value: 'respondido', label: 'Respondido', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'resuelto', label: 'Resuelto', color: 'bg-slate-200 text-slate-700' },
];
const estadoLabel = (v) => ESTADOS.find(e => e.value === v)?.label || v || 'Nuevo';
const estadoColor = (v) => ESTADOS.find(e => e.value === v)?.color || ESTADOS[0].color;

// Extrae el email "puro" desde "Nombre <email@x.com>" o "email@x.com".
function extractEmail(raw) {
  if (!raw) return '';
  const m = String(raw).match(/[\w.+\-]+@[\w.\-]+\.[a-z]{2,}/i);
  return m ? m[0].toLowerCase() : '';
}

export default function BandejaUnificadaPage() {
  const [params, setParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [filterAccount, setFilterAccount] = useState('');
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [scopeFilter, setScopeFilter] = useState('todas'); // 'todas' | 'mias' | 'sin_asignar'
  const [estadoFilter, setEstadoFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [conAdjunto, setConAdjunto] = useState(false);
  const [vinculadoFilter, setVinculadoFilter] = useState(''); // '' | 'si' | 'no'
  const [matriculadoSel, setMatriculadoSel] = useState(null); // {id, nombre, apellido, ...}
  const [matriculadoQ, setMatriculadoQ] = useState('');
  const [matriculadoOpts, setMatriculadoOpts] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDraftId, setComposeDraftId] = useState(null); // reabrir un compose "nuevo" desde Borradores
  const [carpeta, setCarpeta] = useState('entrada'); // 'entrada' | 'enviados' | 'borradores' | 'papelera'
  // Drafts (carpeta "Borradores")
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [etiquetaFilter, setEtiquetaFilter] = useState(''); // filtrar por etiqueta_id (usado por carpetas personalizadas)
  const [etiquetas, setEtiquetas] = useState([]); // catálogo de etiquetas
  const [carpetas, setCarpetas] = useState([]); // carpetas personalizadas
  const [mencionesNoLeidas, setMencionesNoLeidas] = useState(0);
  const [showCarpetaDlg, setShowCarpetaDlg] = useState(null); // null | 'nueva' | carpetaObj
  const [showAsignarEtiquetaDlg, setShowAsignarEtiquetaDlg] = useState(null); // conv en el que abrir picker
  const [remoteSearchOpen, setRemoteSearchOpen] = useState(false);
  const [banner, setBanner] = useState(null);
  const [selectedConv, setSelectedConv] = useState(null);
  const [forwardData, setForwardData] = useState(null); // para reenviar desde el drawer
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // thread_ids seleccionados para bulk
  const [bulkWorking, setBulkWorking] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/bandeja-unificada/accounts`);
      setAccounts(r.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchEtiquetas = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/bandeja-unificada/etiquetas`);
      setEtiquetas(r.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchCarpetas = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/bandeja-unificada/carpetas`);
      setCarpetas(r.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchDrafts = useCallback(async () => {
    setLoadingDrafts(true);
    try {
      const r = await axios.get(`${API_URL}/bandeja-unificada/drafts`);
      setDrafts(r.data?.drafts || []);
    } catch (e) {
      console.error(e);
      setDrafts([]);
    }
    setLoadingDrafts(false);
  }, []);

  const eliminarDraft = useCallback(async (draftId) => {
    try {
      await axios.delete(`${API_URL}/bandeja-unificada/drafts/${draftId}`);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
    } catch (e) { /* silent */ }
  }, []);

  const fetchMencionesNoLeidas = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/bandeja-unificada/menciones/no-leidas/count`);
      setMencionesNoLeidas(r.data?.count || 0);
    } catch (e) { /* silent */ }
  }, []);

  const [totalConvs, setTotalConvs] = useState(0);
  const [pageOffset, setPageOffset] = useState(0);
  const PAGE_LIMIT = 50;

  const fetchConversations = useCallback(async (nextOffset = null) => {
    const offset = nextOffset !== null ? nextOffset : pageOffset;
    try {
      const q = new URLSearchParams();
      if (filterAccount) q.set('account_id', filterAccount);
      if (onlyUnread) q.set('solo_no_leidas', 'true');
      if (scopeFilter === 'mias') q.set('mias', 'true');
      if (scopeFilter === 'sin_asignar') q.set('sin_asignar', 'true');
      if (estadoFilter) q.set('estado', estadoFilter);
      if (searchQ.trim()) q.set('q', searchQ.trim());
      if (desde) q.set('desde', desde);
      if (hasta) q.set('hasta', hasta);
      if (conAdjunto) q.set('con_adjunto', 'true');
      if (vinculadoFilter === 'si') q.set('vinculado', 'true');
      if (vinculadoFilter === 'no') q.set('vinculado', 'false');
      if (matriculadoSel?.id) q.set('trabajador_id', matriculadoSel.id);
      if (carpeta) q.set('carpeta', carpeta);
      if (carpeta === 'papelera') q.set('papelera', 'true');
      if (etiquetaFilter) q.set('etiqueta_id', etiquetaFilter);
      q.set('limit', String(PAGE_LIMIT));
      q.set('offset', String(offset));
      const r = await axios.get(`${API_URL}/bandeja-unificada/conversations?${q}`);
      // Backward compat: si el backend devuelve un array plano (legacy), lo mostramos igual.
      if (Array.isArray(r.data)) {
        setConversations(r.data);
        setTotalConvs(r.data.length);
      } else {
        setConversations(r.data?.items || []);
        setTotalConvs(r.data?.total ?? 0);
      }
    } catch (e) { console.error(e); }
  }, [filterAccount, onlyUnread, scopeFilter, estadoFilter, searchQ, desde, hasta, conAdjunto, vinculadoFilter, matriculadoSel, carpeta, etiquetaFilter, pageOffset]);

  // Al cambiar cualquier filtro, resetear a la primera página.
  useEffect(() => {
    setPageOffset(0);
  }, [filterAccount, onlyUnread, scopeFilter, estadoFilter, searchQ, desde, hasta, conAdjunto, vinculadoFilter, matriculadoSel, carpeta, etiquetaFilter]);

  // Cargar drafts cuando se entra a "Borradores" o cambia el estado
  useEffect(() => {
    if (carpeta === 'borradores') {
      fetchDrafts();
    }
  }, [carpeta, fetchDrafts]);

  // Autocomplete matriculado con debounce
  useEffect(() => {
    if (!matriculadoQ || matriculadoQ.length < 2) { setMatriculadoOpts([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/bandeja-unificada/matriculados/buscar?q=${encodeURIComponent(matriculadoQ)}`);
        setMatriculadoOpts(r.data || []);
      } catch (e) { /* silent */ }
    }, 250);
    return () => clearTimeout(t);
  }, [matriculadoQ]);

  const filtrosActivos = Boolean(
    desde || hasta || conAdjunto || vinculadoFilter || matriculadoSel || searchQ.trim() || estadoFilter || onlyUnread || scopeFilter !== 'todas' || filterAccount
  );
  const filtrosAvanzadosActivos = Boolean(desde || hasta || conAdjunto || vinculadoFilter || matriculadoSel);

  const limpiarAvanzados = () => {
    setDesde(''); setHasta(''); setConAdjunto(false);
    setVinculadoFilter(''); setMatriculadoSel(null); setMatriculadoQ('');
  };

  // ═══ Selección múltiple + bulk read/unread ═══
  const toggleSelect = (threadId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(threadId)) next.delete(threadId); else next.add(threadId);
      return next;
    });
  };

  const allVisibleSelected = conversations.length > 0 && conversations.every(c => selectedIds.has(c.thread_id));
  const someVisibleSelected = conversations.some(c => selectedIds.has(c.thread_id)) && !allVisibleSelected;

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (allVisibleSelected) {
        // deseleccionar solo las visibles (mantener otras si las hubiera)
        const next = new Set(prev);
        conversations.forEach(c => next.delete(c.thread_id));
        return next;
      }
      const next = new Set(prev);
      conversations.forEach(c => next.add(c.thread_id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkMarcarLeido = async (leido) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkWorking(true);
    try {
      await axios.post(`${API_URL}/bandeja-unificada/conversations/bulk/leido`, {
        thread_ids: ids,
        leido,
      });
      // Actualización optimista local
      setConversations(prev => prev.map(c => ids.includes(c.thread_id) ? { ...c, leido } : c));
      setBanner({ tipo: 'ok', msg: `${ids.length} conversación(es) marcada(s) como ${leido ? 'leída(s)' : 'no leída(s)'}` });
      clearSelection();
    } catch (e) {
      setBanner({ tipo: 'err', msg: e.response?.data?.detail || 'Error al actualizar' });
    } finally {
      setBulkWorking(false);
    }
  };

  const bulkEliminar = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(
      `¿Eliminar ${ids.length} conversación(es) de la bandeja?\n\nSolo se ocultarán de la plataforma. ` +
      `Los correos originales se mantienen intactos en la casilla de Gmail/Yahoo.`,
    )) return;
    setBulkWorking(true);
    try {
      await axios.post(`${API_URL}/bandeja-unificada/conversations/bulk/eliminar`, {
        thread_ids: ids,
      });
      setConversations(prev => prev.filter(c => !ids.includes(c.thread_id)));
      setBanner({ tipo: 'ok', msg: `${ids.length} conversación(es) eliminada(s) de la bandeja` });
      clearSelection();
    } catch (e) {
      setBanner({ tipo: 'err', msg: e.response?.data?.detail || 'Error al eliminar' });
    } finally {
      setBulkWorking(false);
    }
  };

  const bulkRestaurar = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkWorking(true);
    try {
      await axios.post(`${API_URL}/bandeja-unificada/conversations/bulk/restaurar`, {
        thread_ids: ids,
      });
      setConversations(prev => prev.filter(c => !ids.includes(c.thread_id)));
      setBanner({ tipo: 'ok', msg: `${ids.length} conversación(es) restaurada(s)` });
      clearSelection();
    } catch (e) {
      setBanner({ tipo: 'err', msg: e.response?.data?.detail || 'Error al restaurar' });
    } finally {
      setBulkWorking(false);
    }
  };

  const bulkEliminarDefinitivamente = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const confirm1 = window.confirm(
      `⚠️ ATENCIÓN: vas a ELIMINAR DEFINITIVAMENTE ${ids.length} conversación(es) y todos sus mensajes de la plataforma.\n\n` +
      `Esta acción NO se puede deshacer.\n\n` +
      `Los correos originales en Gmail/Yahoo NO se tocan; solo se borran de esta base de datos.\n\n` +
      `¿Continuar?`,
    );
    if (!confirm1) return;
    const confirm2 = window.prompt(
      `Para confirmar, escribí ELIMINAR en mayúsculas:`,
    );
    if (confirm2 !== 'ELIMINAR') {
      setBanner({ tipo: 'err', msg: 'Cancelado: no escribiste ELIMINAR' });
      return;
    }
    setBulkWorking(true);
    try {
      const r = await axios.post(
        `${API_URL}/bandeja-unificada/conversations/bulk/eliminar-definitivamente`,
        { thread_ids: ids },
      );
      setConversations(prev => prev.filter(c => !ids.includes(c.thread_id)));
      setBanner({
        tipo: 'ok',
        msg: `${r.data.eliminados} conv(s) y ${r.data.mensajes_borrados} mensaje(s) borrados definitivamente`,
      });
      clearSelection();
    } catch (e) {
      setBanner({ tipo: 'err', msg: e.response?.data?.detail || 'Error al eliminar definitivamente' });
    } finally {
      setBulkWorking(false);
    }
  };

  const vaciarPapelera = async () => {
    const confirm1 = window.confirm(
      `⚠️ Vaciar papelera: se ELIMINARÁN DEFINITIVAMENTE TODAS las conversaciones que estén en la papelera y todos sus mensajes.\n\n` +
      `Esta acción NO se puede deshacer.\n\n` +
      `Los correos originales en Gmail/Yahoo NO se tocan; solo se borran de esta base de datos.\n\n` +
      `¿Continuar?`,
    );
    if (!confirm1) return;
    const confirm2 = window.prompt(`Para confirmar, escribí VACIAR en mayúsculas:`);
    if (confirm2 !== 'VACIAR') {
      setBanner({ tipo: 'err', msg: 'Cancelado: no escribiste VACIAR' });
      return;
    }
    setBulkWorking(true);
    try {
      const r = await axios.post(`${API_URL}/bandeja-unificada/conversations/vaciar-papelera`);
      setConversations([]);
      setBanner({
        tipo: 'ok',
        msg: `Papelera vaciada: ${r.data.eliminados} conv(s) y ${r.data.mensajes_borrados} mensaje(s) borrados`,
      });
      clearSelection();
    } catch (e) {
      setBanner({ tipo: 'err', msg: e.response?.data?.detail || 'Error al vaciar papelera' });
    } finally {
      setBulkWorking(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchAccounts(), fetchConversations(), fetchEtiquetas(), fetchCarpetas(), fetchMencionesNoLeidas()]);
      setLoading(false);
    })();
  }, [fetchAccounts, fetchConversations, fetchEtiquetas, fetchCarpetas, fetchMencionesNoLeidas]);

  // Poll de menciones no-leídas cada 30s
  useEffect(() => {
    const iv = setInterval(fetchMencionesNoLeidas, 30000);
    return () => clearInterval(iv);
  }, [fetchMencionesNoLeidas]);

  // Detectar retorno de OAuth (?oauth=ok | ?oauth=error)
  useEffect(() => {
    const oauthStatus = params.get('oauth');
    if (oauthStatus === 'ok') {
      setBanner({ tipo: 'ok', msg: `Cuenta conectada: ${params.get('cuenta') || ''}` });
      fetchAccounts();
    } else if (oauthStatus === 'error') {
      const reason = params.get('reason') || 'desconocido';
      setBanner({ tipo: 'err', msg: `No se pudo conectar la cuenta: ${reason}` });
    }
  }, [params, fetchAccounts]);

  // Deep-link: si viene ?thread=<id>, abrir la conversación en el drawer.
  useEffect(() => {
    const tid = params.get('thread') || params.get('open_thread');
    if (!tid) return;
    // Se busca en la lista ya cargada; si no está (p.ej. estamos en carpeta="entrada" y
    // el thread es saliente), forzamos carpeta="todas" para verlo.
    const found = conversations.find(c => c.thread_id === tid);
    if (found) {
      setSelectedConv(found);
    } else if (!loading && conversations.length > 0 && carpeta !== 'todas') {
      setCarpeta('todas');
    } else if (!loading) {
      // Como último recurso, abrir con datos mínimos (el drawer hace el fetch por su cuenta).
      setSelectedConv({ thread_id: tid });
    }
  }, [params, conversations, loading, carpeta]);

  // Deep-link: ?draft=<id> abre ComposeDialog reabriendo el borrador
  useEffect(() => {
    const did = params.get('draft');
    if (!did) return;
    setComposeDraftId(did);
    setComposeOpen(true);
    // Limpia el query param para que no reabra en cada render
    const next = new URLSearchParams(params);
    next.delete('draft');
    setParams(next, { replace: true });
  }, [params, setParams]);

  const syncOne = async (id) => {
    setSyncingId(id);
    try {
      // Timeout amplio: el backend tiene un límite de 80s por cuenta.
      // El default global de axios (30s) cortaba antes y mostraba "Error" genérico.
      const r = await axios.post(`${API_URL}/bandeja-unificada/accounts/${id}/sync`, null, { timeout: 100000 });
      await Promise.all([fetchAccounts(), fetchConversations()]);
      const nuevos = r?.data?.nuevos ?? 0;
      setBanner({ tipo: 'ok', msg: nuevos > 0 ? `Sincronización OK · ${nuevos} nuevos` : 'Sincronización OK · sin novedades' });
    } catch (e) {
      // Recargar accounts para reflejar ultimo_sync_error en la lista
      fetchAccounts();
      const detail = e.response?.data?.detail;
      let msg;
      if (detail) msg = detail;
      else if (e.code === 'ECONNABORTED') msg = 'Timeout: el servidor de correo no respondió a tiempo (>100s)';
      else msg = e.message || 'Error al sincronizar';
      setBanner({ tipo: 'err', msg });
    } finally { setSyncingId(null); }
  };

  const syncAll = async () => {
    setSyncingAll(true);
    try {
      // Timeout amplio (100s). El backend paraleliza cuentas y cada una tiene tope de 80s.
      const r = await axios.post(`${API_URL}/bandeja-unificada/sync-all`, null, { timeout: 100000 });
      await Promise.all([fetchAccounts(), fetchConversations()]);
      const stats = r?.data || {};
      const errores = stats.errores || [];
      const nuevos = stats.mensajes_nuevos || 0;
      if (errores.length > 0) {
        const detalle = errores.map(e => `${e.account}: ${e.error}`).join(' · ');
        setBanner({
          tipo: 'err',
          msg: `Sincronización con errores (${errores.length}/${stats.cuentas || 0} cuentas fallaron). ${nuevos} nuevos correos. Detalle: ${detalle}`,
        });
      } else {
        setBanner({
          tipo: 'ok',
          msg: `Sincronización OK · ${stats.cuentas || 0} cuentas · ${nuevos} correos nuevos`,
        });
      }
    } catch (e) {
      fetchAccounts();
      const detail = e.response?.data?.detail;
      let msg;
      if (detail) msg = detail;
      else if (e.code === 'ECONNABORTED') msg = 'Timeout: la sincronización tardó más de 100s. Alguna cuenta puede estar lenta.';
      else msg = e.message || 'Error de red al sincronizar';
      setBanner({ tipo: 'err', msg });
    } finally { setSyncingAll(false); }
  };

  const deleteAccount = async (a) => {
    if (!window.confirm(`¿Eliminar la cuenta "${a.label}" (${a.email})? Se borrarán también las conversaciones sincronizadas.`)) return;
    try {
      await axios.delete(`${API_URL}/bandeja-unificada/accounts/${a.id}`);
      await Promise.all([fetchAccounts(), fetchConversations()]);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="space-y-4" data-testid="bandeja-unificada-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pl-12 lg:pl-0">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2 min-w-0">
            <Inbox className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 flex-shrink-0" /> <span className="truncate">Bandeja Institucional Unificada</span>
          </h1>
          <p className="text-sm text-gray-600 hidden sm:block">Correos de todas las cuentas conectadas en un solo lugar.</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button size="sm" onClick={() => setComposeOpen(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-compose-new">
            <Edit3 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Redactar</span>
          </Button>
          <Button size="sm" variant="outline" onClick={syncAll} disabled={syncingAll || accounts.length === 0} data-testid="btn-sync-all" title="Sincronizar">
            {syncingAll ? <Loader2 className="w-4 h-4 animate-spin sm:mr-1" /> : <RefreshCw className="w-4 h-4 sm:mr-1" />}
            <span className="hidden sm:inline">Sincronizar</span>
          </Button>
          <Link to="/admin/bandeja-unificada/menciones" className="relative inline-flex items-center gap-1 px-2 sm:px-3 py-1.5 border rounded hover:bg-gray-50 text-sm" data-testid="btn-nav-menciones" title="Mis menciones">
            <AtSign className="w-4 h-4" /> <span className="hidden md:inline">Menciones</span>
            {mencionesNoLeidas > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="badge-menciones">
                {mencionesNoLeidas > 99 ? '99+' : mencionesNoLeidas}
              </span>
            )}
          </Link>
          <Link to="/admin/bandeja-unificada/contactos-etiquetas" className="inline-flex items-center gap-1 px-2 sm:px-3 py-1.5 border rounded hover:bg-gray-50 text-sm" data-testid="btn-nav-contactos-etiquetas" title="Contactos, Etiquetas y Reglas">
            <Tag className="w-4 h-4" /> <span className="hidden md:inline">Etiquetas</span>
          </Link>
          <Link to="/admin/bandeja-unificada/configuracion" className="inline-flex items-center gap-1 px-2 sm:px-3 py-1.5 border rounded hover:bg-gray-50 text-sm" data-testid="btn-nav-config" title="Configuración">
            <Settings className="w-4 h-4" /> <span className="hidden md:inline">Configuración</span>
          </Link>
        </div>
      </div>

      {banner && (
        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${banner.tipo === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="bandeja-banner">
          <span className="flex-1 break-words whitespace-pre-wrap">{banner.msg}</span>
          <button
            onClick={() => setBanner(null)}
            className="flex-shrink-0 hover:opacity-70"
            aria-label="Cerrar"
            data-testid="banner-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="lg:hidden mb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFiltersMobile(!showFiltersMobile)}
          className="w-full justify-between"
          data-testid="btn-toggle-filters-mobile"
        >
          <span className="flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filtros y cuentas
          </span>
          <span className="text-xs text-gray-500">
            {showFiltersMobile ? 'Ocultar' : 'Mostrar'}
          </span>
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        {/* Sidebar cuentas - oculta en mobile a menos que se togglee */}
        <Card className={`lg:col-span-1 min-w-0 ${showFiltersMobile ? '' : 'hidden lg:block'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Filtros rápidos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 pb-3">
            {/* Carpetas */}
            <div className="pb-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 px-1">Carpeta</p>
              {[
                { v: 'entrada', label: 'Recibidos', icon: Inbox },
                { v: 'enviados', label: 'Enviados', icon: Send },
                { v: 'borradores', label: 'Borradores', icon: FileText },
                { v: 'papelera', label: 'Papelera', icon: Trash2 },
              ].map(f => {
                const Icon = f.icon;
                return (
                  <button
                    key={f.v}
                    onClick={() => setCarpeta(f.v)}
                    className={`w-full text-left px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 ${carpeta === f.v ? 'bg-purple-100 text-purple-800 font-medium' : 'hover:bg-gray-50'}`}
                    data-testid={`filter-carpeta-${f.v}`}
                  >
                    <Icon className="w-4 h-4" /> {f.label}
                  </button>
                );
              })}
            </div>
            {/* Carpetas personalizadas */}
            <div className="pt-1 pb-1">
              <div className="flex items-center justify-between px-1 mb-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Carpetas personalizadas</p>
                <button
                  onClick={() => setShowCarpetaDlg('nueva')}
                  className="text-gray-500 hover:text-purple-600"
                  title="Nueva carpeta"
                  data-testid="btn-nueva-carpeta"
                >
                  <FolderPlus className="w-3.5 h-3.5" />
                </button>
              </div>
              {carpetas.length === 0 ? (
                <p className="text-[10px] text-gray-400 italic px-2 py-1">Sin carpetas. Creá una basada en etiquetas.</p>
              ) : carpetas.map(c => {
                const activa = c.etiquetas?.length === 1 && etiquetaFilter === c.etiquetas[0];
                return (
                  <div key={c.id} className={`flex items-center group rounded-lg ${activa ? 'bg-purple-100' : 'hover:bg-gray-50'}`}>
                    <button
                      onClick={() => {
                        if (c.etiquetas?.length > 0) {
                          setEtiquetaFilter(c.etiquetas[0]);
                          setCarpeta('todas');
                        }
                      }}
                      className="flex-1 text-left px-3 py-1.5 text-sm flex items-center gap-2 min-w-0"
                      data-testid={`filter-carpeta-personal-${c.id}`}
                      title={c.compartida ? 'Carpeta compartida' : 'Carpeta personal'}
                    >
                      <Folder className="w-4 h-4 shrink-0" style={{ color: c.color }} />
                      <span className={`truncate ${activa ? 'font-medium text-purple-800' : ''}`}>{c.nombre}</span>
                      {c.compartida && <Users className="w-3 h-3 text-gray-400 shrink-0" />}
                    </button>
                    <button
                      onClick={() => setShowCarpetaDlg(c)}
                      className="opacity-0 group-hover:opacity-100 px-1 text-gray-400 hover:text-gray-700"
                      title="Editar"
                      data-testid={`btn-editar-carpeta-${c.id}`}
                    >
                      <Edit3 className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
            {/* Filtro por etiqueta */}
            {etiquetas.length > 0 && (
              <div className="pt-1 pb-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 px-1">Etiquetas</p>
                {etiquetaFilter && (
                  <button
                    onClick={() => setEtiquetaFilter('')}
                    className="w-full text-left px-3 py-1 rounded-lg text-xs text-purple-600 hover:bg-gray-50 flex items-center gap-1"
                    data-testid="btn-clear-etiqueta"
                  >
                    <X className="w-3 h-3" /> Limpiar filtro
                  </button>
                )}
                <div className="flex flex-wrap gap-1 px-1">
                  {etiquetas.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setEtiquetaFilter(etiquetaFilter === t.id ? '' : t.id)}
                      className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 border ${etiquetaFilter === t.id ? 'ring-2 ring-offset-1' : ''}`}
                      style={{
                        background: etiquetaFilter === t.id ? t.color : `${t.color}22`,
                        color: etiquetaFilter === t.id ? 'white' : t.color,
                        borderColor: `${t.color}55`,
                      }}
                      data-testid={`filter-etiqueta-${t.id}`}
                    >
                      <Tag className="w-2.5 h-2.5" />{t.nombre}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 px-1 pt-1">Alcance</p>
            {[
              { v: 'todas', label: 'Todas', icon: Inbox },
              { v: 'mias', label: 'Mis conversaciones', icon: UserCircle },
              { v: 'sin_asignar', label: 'Sin asignar', icon: AlertCircle },
            ].map(f => {
              const Icon = f.icon;
              return (
                <button
                  key={f.v}
                  onClick={() => setScopeFilter(f.v)}
                  className={`w-full text-left px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 ${scopeFilter === f.v ? 'bg-purple-100 text-purple-800 font-medium' : 'hover:bg-gray-50'}`}
                  data-testid={`filter-scope-${f.v}`}
                >
                  <Icon className="w-4 h-4" /> {f.label}
                </button>
              );
            })}
            <div className="pt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 px-1">Estado</p>
              <select
                value={estadoFilter}
                onChange={e => setEstadoFilter(e.target.value)}
                className="w-full text-sm p-1.5 border rounded-lg bg-white"
                data-testid="filter-estado"
              >
                <option value="">Todos los estados</option>
                {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </div>
          </CardContent>
          <div className="border-t px-6 pt-3 pb-1">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cuentas ({accounts.length})</p>
          </div>
          <CardContent className="space-y-2 pt-2">
            <button
              onClick={() => setFilterAccount('')}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${!filterAccount ? 'bg-purple-50 text-purple-800 font-medium' : 'hover:bg-gray-50'}`}
              data-testid="filter-all-accounts"
            >
              Todas las cuentas
            </button>
            {accounts.map(a => (
              <div
                key={a.id}
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${filterAccount === a.id ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
              >
                <button
                  onClick={() => setFilterAccount(a.id)}
                  className="flex-1 flex items-center gap-2 min-w-0 text-left"
                  data-testid={`filter-account-${a.id}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: a.color || '#7c3aed' }}></span>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 truncate">{a.label}</p>
                    <p className="text-xs text-gray-500 truncate">{a.email}</p>
                    {a.rate_limited_until && new Date(a.rate_limited_until) > new Date() && (
                      <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5 mt-0.5 flex items-start gap-1" data-testid={`rate-limited-${a.id}`}>
                        <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">
                          Pausada por rate-limit hasta {new Date(a.rate_limited_until).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </p>
                    )}
                    {a.ultimo_sync_ok === false && a.ultimo_sync_error && !a.rate_limited_until && (
                      <p className="text-[10px] text-red-600 mt-0.5 flex items-start gap-1" title={a.ultimo_sync_error} data-testid={`sync-error-${a.id}`}>
                        <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">{a.ultimo_sync_error}</span>
                      </p>
                    )}
                  </div>
                </button>
                {a.rate_limited_until && new Date(a.rate_limited_until) > new Date() ? (
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API_URL}/bandeja-unificada/accounts/${a.id}/reanudar-sync`);
                        fetchAccounts();
                      } catch (e) { alert(e.response?.data?.detail || 'Error'); }
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 text-amber-600 hover:text-amber-800"
                    title="Reanudar sync (quitar pausa por rate-limit)"
                    data-testid={`resume-account-${a.id}`}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    onClick={() => syncOne(a.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-purple-600"
                    disabled={syncingId === a.id}
                    title="Sincronizar esta cuenta"
                    data-testid={`sync-account-${a.id}`}
                  >
                    {syncingId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </button>
                )}
                <button
                  onClick={() => deleteAccount(a)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600"
                  title="Eliminar cuenta"
                  data-testid={`delete-account-${a.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {accounts.length === 0 && (
              <p className="text-xs text-gray-500 italic px-2 py-4 text-center">No hay cuentas conectadas. Agregá una desde <Link to="/admin/bandeja-unificada/configuracion" className="underline text-purple-600">Configuración</Link>.</p>
            )}
          </CardContent>
        </Card>

        {/* Lista de conversaciones */}
        <Card className="lg:col-span-3 min-w-0">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2 flex-wrap min-w-0">
              <CardTitle className="text-base flex items-center gap-2">
                <Mail className="w-4 h-4" /> {
                  carpeta === 'papelera' ? 'Papelera'
                  : carpeta === 'enviados' ? 'Enviados'
                  : carpeta === 'todas' ? 'Todos los correos'
                  : 'Bandeja de entrada'
                } ({conversations.length})
                {carpeta === 'papelera' && conversations.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={vaciarPapelera}
                    disabled={bulkWorking}
                    className="h-7 text-xs border-red-500 text-red-700 bg-red-50 hover:bg-red-100 hover:text-red-800 ml-2"
                    data-testid="btn-vaciar-papelera"
                  >
                    {bulkWorking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                    Vaciar papelera
                  </Button>
                )}
              </CardTitle>
              <div className="flex items-center gap-2 flex-1 min-w-0 basis-full sm:basis-auto sm:max-w-md flex-wrap">
                <div className="relative flex-1 min-w-0">
                  <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Buscar en asunto o cuerpo…"
                    value={searchQ}
                    onChange={e => setSearchQ(e.target.value)}
                    className="pl-8 h-9 text-sm"
                    data-testid="input-search-inbox"
                  />
                </div>
                <label className="text-xs flex items-center gap-1.5 text-gray-600 cursor-pointer whitespace-nowrap">
                  <input type="checkbox" checked={onlyUnread} onChange={e => setOnlyUnread(e.target.checked)} data-testid="filter-only-unread" />
                  Solo no leídos
                </label>
                <button
                  onClick={() => setShowAdvanced(o => !o)}
                  className={`text-xs px-2.5 py-1.5 rounded border inline-flex items-center gap-1 ${filtrosAvanzadosActivos ? 'bg-purple-100 border-purple-300 text-purple-800 font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                  data-testid="btn-toggle-advanced"
                >
                  <Filter className="w-3.5 h-3.5" />
                  Avanzados
                  {filtrosAvanzadosActivos && (
                    <span className="ml-1 bg-purple-600 text-white rounded-full text-[10px] px-1.5" data-testid="badge-advanced-count">
                      {[desde && 'desde', hasta && 'hasta', conAdjunto && 'adj', vinculadoFilter, matriculadoSel && 'mat'].filter(Boolean).length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {showAdvanced && (
              <div className="mt-3 border rounded-lg bg-slate-50 p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="advanced-filters-panel">
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Desde</label>
                  <input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="w-full border rounded px-2 py-1 text-sm bg-white" data-testid="filter-desde" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Hasta</label>
                  <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="w-full border rounded px-2 py-1 text-sm bg-white" data-testid="filter-hasta" />
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Adjuntos</label>
                  <label className="flex items-center gap-2 text-sm px-2 py-1 border rounded bg-white cursor-pointer">
                    <input type="checkbox" checked={conAdjunto} onChange={e => setConAdjunto(e.target.checked)} data-testid="filter-con-adjunto" />
                    <span>Solo con adjuntos 📎</span>
                  </label>
                </div>
                <div>
                  <label className="text-xs text-gray-600 block mb-1">Vinculación</label>
                  <select value={vinculadoFilter} onChange={e => setVinculadoFilter(e.target.value)} className="w-full border rounded px-2 py-1 text-sm bg-white" data-testid="filter-vinculado">
                    <option value="">Todos</option>
                    <option value="si">Solo vinculados a matriculado</option>
                    <option value="no">Solo sin vincular</option>
                  </select>
                </div>
                <div className="sm:col-span-2 md:col-span-4">
                  <label className="text-xs text-gray-600 block mb-1">Filtrar por matriculado específico</label>
                  {matriculadoSel ? (
                    <div className="inline-flex items-center gap-2 bg-purple-100 border border-purple-300 rounded px-2 py-1 text-sm" data-testid="filter-matriculado-selected">
                      <UserCircle className="w-4 h-4 text-purple-700" />
                      <span className="font-medium">{matriculadoSel.apellido}, {matriculadoSel.nombre}</span>
                      {matriculadoSel.numero_matricula && <span className="text-xs text-purple-700">Mat. {matriculadoSel.numero_matricula}</span>}
                      <button onClick={() => { setMatriculadoSel(null); setMatriculadoQ(''); }} className="ml-1 text-red-600 hover:text-red-800" data-testid="btn-quitar-matriculado">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        value={matriculadoQ}
                        onChange={e => setMatriculadoQ(e.target.value)}
                        placeholder="Buscar por DNI, matrícula, nombre o apellido…"
                        className="w-full border rounded px-2 py-1 text-sm bg-white"
                        data-testid="filter-matriculado-input"
                      />
                      {matriculadoOpts.length > 0 && (
                        <div className="absolute z-10 mt-1 bg-white border rounded shadow max-h-56 overflow-y-auto w-full" data-testid="filter-matriculado-opts">
                          {matriculadoOpts.map(t => (
                            <button
                              key={t.id}
                              onClick={() => { setMatriculadoSel(t); setMatriculadoQ(''); setMatriculadoOpts([]); }}
                              className="w-full text-left px-2 py-1.5 hover:bg-purple-50 border-b last:border-b-0"
                              data-testid={`filter-matriculado-opt-${t.id}`}
                            >
                              <p className="text-sm font-medium">{t.apellido}, {t.nombre}</p>
                              <p className="text-xs text-gray-500">
                                Mat. {t.numero_matricula || '—'} · DNI {t.documento || '—'} · {t.email || 'sin email'}
                              </p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2 md:col-span-4 flex justify-end">
                  <button onClick={limpiarAvanzados} className="text-xs text-gray-600 hover:text-red-600" data-testid="btn-limpiar-avanzados">
                    Limpiar filtros avanzados
                  </button>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {/* Banner de búsqueda remota (siempre visible cuando hay búsqueda activa) */}
            {searchQ.trim() && (
              <div className="flex items-center justify-between gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs" data-testid="banner-busqueda-remota">
                <div className="flex items-center gap-2 min-w-0 text-amber-900">
                  <Database className="w-4 h-4 shrink-0" />
                  <span className="truncate">
                    Buscando <strong>"{searchQ}"</strong> en la bandeja local. ¿No encontrás lo que buscás?
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRemoteSearchOpen(true)}
                  disabled={!accounts.some(a => a.provider === 'yahoo' || a.auth_type === 'imap_smtp')}
                  className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
                  title={accounts.some(a => a.provider === 'yahoo' || a.auth_type === 'imap_smtp') ? 'Consulta IMAP en tiempo real (no guarda resultados)' : 'Se necesita al menos una cuenta IMAP'}
                  data-testid="btn-buscar-remoto"
                >
                  <Database className="w-3.5 h-3.5 mr-1" /> Buscar en servidor
                </Button>
              </div>
            )}
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" /></div>
            ) : carpeta === 'borradores' ? (
              // ── Carpeta Borradores ──
              loadingDrafts ? (
                <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" /></div>
              ) : drafts.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500 space-y-2" data-testid="drafts-empty">
                  <FileText className="w-10 h-10 mx-auto mb-1 text-gray-300" />
                  <p>No tenés borradores guardados.</p>
                  <p className="text-xs">Cuando empieces a escribir una respuesta, se guarda automáticamente acá.</p>
                </div>
              ) : (
                <div data-testid="lista-borradores">
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b bg-purple-50/40 text-xs text-purple-800">
                    <span data-testid="total-borradores">
                      <strong>{drafts.length}</strong> {drafts.length === 1 ? 'borrador' : 'borradores'}
                    </span>
                    <button
                      onClick={fetchDrafts}
                      className="text-purple-700 hover:underline flex items-center gap-1"
                      data-testid="btn-refresh-drafts"
                    >
                      <RefreshCw className="w-3 h-3" /> Actualizar
                    </button>
                  </div>
                  {drafts.map(d => {
                    const fecha = d.updated_at ? new Date(d.updated_at).toLocaleString('es-AR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '';
                    const isNew = (d.thread_id || '').startsWith('nuevo:');
                    return (
                      <div
                        key={d.id}
                        className="border-b hover:bg-purple-50 px-4 py-3 flex items-start justify-between gap-2 cursor-pointer"
                        onClick={() => {
                          if (isNew) {
                            // Reabrir compose "nuevo" con el borrador pre-cargado
                            setComposeDraftId(d.id);
                            setComposeOpen(true);
                          } else if (d.thread_id) {
                            setParams({ thread: d.thread_id });
                          }
                        }}
                        data-testid={`draft-item-${d.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <FileText className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                            <p className="font-medium text-sm text-gray-800 truncate">
                              {d.subject || d.conv_asunto || <span className="italic text-gray-400">(sin asunto)</span>}
                            </p>
                            {isNew && (
                              <span className="text-[10px] bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded uppercase tracking-wide">
                                nuevo
                              </span>
                            )}
                          </div>
                          {(d.to || []).length > 0 && (
                            <p className="text-xs text-gray-500 truncate">Para: {(d.to || []).join(', ')}</p>
                          )}
                          {d.preview && (
                            <p className="text-xs text-gray-600 truncate mt-0.5">{d.preview}</p>
                          )}
                          <p className="text-[10px] text-gray-400 mt-1">Actualizado {fecha}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); eliminarDraft(d.id); }}
                          className="text-gray-400 hover:text-red-500 flex-shrink-0"
                          title="Eliminar borrador"
                          data-testid={`btn-eliminar-draft-${d.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )
            ) : conversations.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-500 space-y-3">
                <Inbox className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                {searchQ.trim()
                  ? <p>Sin resultados locales para <strong>"{searchQ}"</strong>. Probá con la búsqueda remota del banner superior.</p>
                  : <>No hay conversaciones aún. Sincronizá una cuenta para verlas acá.</>
                }
              </div>
            ) : (
              <div data-testid="lista-conversaciones">
                {/* Encabezado con total + controles de paginación (arriba) */}
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b bg-purple-50/40 text-xs text-purple-800">
                  <span data-testid="total-conversaciones">
                    <strong>{totalConvs.toLocaleString('es-AR')}</strong> {totalConvs === 1 ? 'conversación' : 'conversaciones'} en total
                    {totalConvs > PAGE_LIMIT && (
                      <> · mostrando <strong>{pageOffset + 1}–{Math.min(pageOffset + conversations.length, totalConvs)}</strong></>
                    )}
                  </span>
                  {totalConvs > PAGE_LIMIT && (
                    <div className="flex items-center gap-1" data-testid="paginacion-top">
                      <span className="text-purple-700 mr-1">
                        Pág. <strong>{Math.floor(pageOffset / PAGE_LIMIT) + 1}</strong>/<strong>{Math.ceil(totalConvs / PAGE_LIMIT)}</strong>
                      </span>
                      <button
                        onClick={() => setPageOffset(0)}
                        disabled={pageOffset === 0}
                        className="px-2 py-0.5 border border-purple-200 rounded disabled:opacity-40 hover:bg-white"
                        data-testid="btn-pag-top-first"
                        title="Primera página"
                      >««</button>
                      <button
                        onClick={() => setPageOffset(Math.max(0, pageOffset - PAGE_LIMIT))}
                        disabled={pageOffset === 0}
                        className="px-2 py-0.5 border border-purple-200 rounded disabled:opacity-40 hover:bg-white"
                        data-testid="btn-pag-top-prev"
                      >‹ Anterior</button>
                      <button
                        onClick={() => setPageOffset(pageOffset + PAGE_LIMIT)}
                        disabled={pageOffset + PAGE_LIMIT >= totalConvs}
                        className="px-2 py-0.5 border border-purple-200 rounded disabled:opacity-40 hover:bg-white"
                        data-testid="btn-pag-top-next"
                      >Siguiente ›</button>
                      <button
                        onClick={() => setPageOffset(Math.floor((totalConvs - 1) / PAGE_LIMIT) * PAGE_LIMIT)}
                        disabled={pageOffset + PAGE_LIMIT >= totalConvs}
                        className="px-2 py-0.5 border border-purple-200 rounded disabled:opacity-40 hover:bg-white"
                        data-testid="btn-pag-top-last"
                        title="Última página"
                      >»»</button>
                    </div>
                  )}
                </div>
                {/* Toolbar de selección múltiple */}
                <div className="flex items-center gap-3 px-4 py-2 border-b bg-gray-50/50 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer select-none" title="Seleccionar todo">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-purple-600 cursor-pointer"
                      checked={allVisibleSelected}
                      ref={el => { if (el) el.indeterminate = someVisibleSelected; }}
                      onChange={toggleSelectAll}
                      data-testid="bulk-select-all"
                    />
                    <span className="text-xs text-gray-600">
                      {selectedIds.size > 0 ? `${selectedIds.size} seleccionada${selectedIds.size === 1 ? '' : 's'}` : 'Seleccionar todo'}
                    </span>
                  </label>
                  {selectedIds.size > 0 && (
                    <div className="flex items-center gap-2 ml-auto">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => bulkMarcarLeido(true)}
                        disabled={bulkWorking}
                        className="h-7 text-xs"
                        data-testid="bulk-marcar-leidos"
                      >
                        {bulkWorking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MailOpen className="w-3 h-3 mr-1" />}
                        Marcar como leídos
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => bulkMarcarLeido(false)}
                        disabled={bulkWorking}
                        className="h-7 text-xs"
                        data-testid="bulk-marcar-no-leidos"
                      >
                        {bulkWorking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                        Marcar como no leídos
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={carpeta === 'papelera' ? bulkRestaurar : bulkEliminar}
                        disabled={bulkWorking}
                        className={carpeta === 'papelera'
                          ? "h-7 text-xs border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                          : "h-7 text-xs border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"}
                        data-testid={carpeta === 'papelera' ? "bulk-restaurar" : "bulk-eliminar"}
                      >
                        {bulkWorking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : (carpeta === 'papelera' ? <RefreshCw className="w-3 h-3 mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />)}
                        {carpeta === 'papelera' ? 'Restaurar' : 'Eliminar'}
                      </Button>
                      {carpeta === 'papelera' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={bulkEliminarDefinitivamente}
                          disabled={bulkWorking}
                          className="h-7 text-xs border-red-500 text-red-700 bg-red-50 hover:bg-red-100 hover:text-red-800"
                          data-testid="bulk-eliminar-definitivamente"
                        >
                          {bulkWorking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                          Eliminar definitivamente
                        </Button>
                      )}
                      <button
                        onClick={clearSelection}
                        className="text-xs text-gray-500 hover:text-gray-800"
                        data-testid="bulk-limpiar-seleccion"
                      >
                        Limpiar
                      </button>
                    </div>
                  )}
                </div>

                <div className="divide-y">
                  {conversations.map(c => (
                    <div
                      key={c.thread_id}
                      className={`flex items-center gap-2 px-3 sm:px-4 py-3 hover:bg-purple-50/30 overflow-hidden min-w-0 ${selectedIds.has(c.thread_id) ? 'bg-purple-50/40' : ''}`}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-purple-600 cursor-pointer flex-shrink-0"
                        checked={selectedIds.has(c.thread_id)}
                        onChange={(e) => { e.stopPropagation(); toggleSelect(c.thread_id); }}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Seleccionar conversación"
                        data-testid={`conv-check-${c.thread_id}`}
                      />
                      <button
                        type="button"
                        className="flex-1 min-w-0 text-left flex items-center gap-2 sm:gap-3 cursor-pointer"
                        onClick={() => setSelectedConv(c)}
                        data-testid={`conv-row-${c.thread_id}`}
                      >
                        <span className="flex-shrink-0" title={!c.leido ? 'No leído' : 'Leído'}>
                          {!c.leido ? <CircleDot className="w-3 h-3 text-purple-600 fill-purple-600" /> : <div className="w-3 h-3" />}
                        </span>
                        <span className="w-1.5 sm:w-2 h-8 rounded flex-shrink-0" style={{ background: c.account_color || '#7c3aed' }} title={c.account_label}></span>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap min-w-0">
                            <p className={`text-sm truncate flex-1 min-w-0 basis-full sm:basis-auto ${!c.leido ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                              {c.es_saliente ? (
                                <><span className="text-purple-600 mr-1">↳</span>Para: {c.destinatario_resumen || '(varios)'}</>
                              ) : (c.remitente || '(sin remitente)')}
                            </p>
                            <span className="text-[10px] sm:text-xs px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: (c.account_color || '#7c3aed') + '20', color: c.account_color || '#7c3aed' }}>
                              {c.account_label}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 hidden sm:inline ${estadoColor(c.estado)}`}>
                              {estadoLabel(c.estado)}
                            </span>
                            {c.responsable_id && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 flex-shrink-0 hidden sm:inline" title={c.responsable_snapshot?.email || c.responsable_id}>
                                <UserCircle className="w-2.5 h-2.5 inline mr-0.5" />
                                {c.responsable_snapshot?.nombre || c.responsable_snapshot?.email || c.responsable_id}
                              </span>
                            )}
                            {c.tiene_adjuntos && (
                              <span className="text-[10px] flex-shrink-0" title="Contiene adjuntos" data-testid={`conv-adjunto-${c.thread_id}`}>📎</span>
                            )}
                            {c.tipo_envio === 'masivo' && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 flex-shrink-0" title={`Envío masivo · ${c.n_destinatarios || 0} destinatarios`} data-testid={`conv-masivo-${c.thread_id}`}>
                                Masivo · {c.n_destinatarios || 0}
                              </span>
                            )}
                          </div>
                          <p className={`text-sm truncate ${!c.leido ? 'text-gray-900' : 'text-gray-600'}`}>{c.asunto || '(sin asunto)'}</p>
                          {/* Chips de etiquetas */}
                          {c.etiquetas && c.etiquetas.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {c.etiquetas.map(tid => {
                                const t = etiquetas.find(x => x.id === tid);
                                if (!t) return null;
                                return (
                                  <span
                                    key={tid}
                                    className="text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5"
                                    style={{ background: `${t.color}22`, color: t.color }}
                                    data-testid={`conv-etiqueta-${c.thread_id}-${tid}`}
                                  >
                                    <Tag className="w-2.5 h-2.5" />{t.nombre}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          {/* Fecha visible en mobile como línea inferior compacta */}
                          <p className="text-[10px] text-gray-400 sm:hidden mt-0.5">
                            {c.ultimo_mensaje_at ? new Date(c.ultimo_mensaje_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0 hidden sm:inline">
                          {c.ultimo_mensaje_at ? new Date(c.ultimo_mensaje_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                        {c.matriculado_identificado && (
                          <span title="Matriculado identificado" className="flex-shrink-0">
                            <UserCircle className="w-4 h-4 text-emerald-600" />
                          </span>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
                {/* Paginación */}
                {totalConvs > PAGE_LIMIT && (
                  <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 text-sm" data-testid="paginacion">
                    <div className="text-gray-600 text-xs">
                      Página <strong>{Math.floor(pageOffset / PAGE_LIMIT) + 1}</strong> de <strong>{Math.ceil(totalConvs / PAGE_LIMIT)}</strong>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setPageOffset(0)}
                        disabled={pageOffset === 0}
                        className="px-2 py-1 border rounded text-xs disabled:opacity-40 hover:bg-white"
                        data-testid="btn-pag-first"
                        title="Primera página"
                      >««</button>
                      <button
                        onClick={() => setPageOffset(Math.max(0, pageOffset - PAGE_LIMIT))}
                        disabled={pageOffset === 0}
                        className="px-3 py-1 border rounded text-xs disabled:opacity-40 hover:bg-white"
                        data-testid="btn-pag-prev"
                      >Anterior</button>
                      <button
                        onClick={() => setPageOffset(pageOffset + PAGE_LIMIT)}
                        disabled={pageOffset + PAGE_LIMIT >= totalConvs}
                        className="px-3 py-1 border rounded text-xs disabled:opacity-40 hover:bg-white"
                        data-testid="btn-pag-next"
                      >Siguiente</button>
                      <button
                        onClick={() => setPageOffset(Math.floor((totalConvs - 1) / PAGE_LIMIT) * PAGE_LIMIT)}
                        disabled={pageOffset + PAGE_LIMIT >= totalConvs}
                        className="px-2 py-1 border rounded text-xs disabled:opacity-40 hover:bg-white"
                        data-testid="btn-pag-last"
                        title="Última página"
                      >»»</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Drawer de detalle de conversación */}
      {selectedConv && (
        <ConversationDetailDrawer
          conv={selectedConv}
          onClose={() => setSelectedConv(null)}
          onUpdate={() => { fetchConversations(); }}
          onForward={(data) => setForwardData(data)}
          etiquetasCatalogo={etiquetas}
          onAsignarEtiquetas={(conv) => setShowAsignarEtiquetaDlg(conv)}
        />
      )}

      {/* Modal Búsqueda Remota IMAP */}
      {remoteSearchOpen && (
        <RemoteSearchDialog
          accounts={accounts.filter(a => a.provider === 'yahoo' || a.auth_type === 'imap_smtp')}
          initialQuery={searchQ}
          onClose={() => setRemoteSearchOpen(false)}
        />
      )}

      {/* Modal Redactar (correo nuevo) */}
      <ComposeDialog
        open={composeOpen || Boolean(forwardData)}
        onClose={() => { setComposeOpen(false); setForwardData(null); setComposeDraftId(null); if (carpeta === 'borradores') fetchDrafts(); }}
        onSent={() => { fetchConversations(); if (carpeta === 'borradores') fetchDrafts(); }}
        accounts={accounts}
        initialData={composeDraftId ? { draft_id: composeDraftId } : forwardData}
      />

      {/* Modal Carpeta Personalizada */}
      {showCarpetaDlg && (
        <CarpetaPersonalizadaDialog
          carpeta={showCarpetaDlg === 'nueva' ? null : showCarpetaDlg}
          etiquetas={etiquetas}
          onClose={() => setShowCarpetaDlg(null)}
          onSaved={() => { setShowCarpetaDlg(null); fetchCarpetas(); }}
        />
      )}

      {/* Modal Asignar etiquetas a conversación */}
      {showAsignarEtiquetaDlg && (
        <AsignarEtiquetasDialog
          conv={showAsignarEtiquetaDlg}
          etiquetas={etiquetas}
          onClose={() => setShowAsignarEtiquetaDlg(null)}
          onUpdated={(nuevasEtiquetas) => {
            fetchConversations();
            // Actualizar selectedConv para que el drawer refleje los chips al instante.
            if (selectedConv && selectedConv.thread_id === showAsignarEtiquetaDlg.thread_id) {
              setSelectedConv({ ...selectedConv, etiquetas: nuevasEtiquetas });
            }
            setShowAsignarEtiquetaDlg(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Modal Búsqueda Remota IMAP ───
function RemoteSearchDialog({ accounts, initialQuery, onClose }) {
  const [accountId, setAccountId] = useState(accounts[0]?.id || '');
  const [q, setQ] = useState(initialQuery || '');
  const [desde, setDesde] = useState('2012-01-01');
  const [hasta, setHasta] = useState('2021-12-31');
  const [searching, setSearching] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [error, setError] = useState('');

  const buscar = async () => {
    if (!accountId) { setError('Elegí una cuenta'); return; }
    setSearching(true); setError(''); setResultados([]);
    try {
      const r = await axios.get(`${API_URL}/bandeja-unificada/remote-search`, {
        params: { account_id: accountId, q, desde, hasta, limit: 100 },
        timeout: 120000,
      });
      setResultados(r.data?.resultados || []);
    } catch (e) { setError(e.response?.data?.detail || 'Error al buscar'); }
    setSearching(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-remote-search">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Database className="w-5 h-5 text-purple-600" /> Buscar en servidor remoto</DialogTitle>
          <DialogDescription>Consulta IMAP en tiempo real sobre el histórico. No guarda los resultados salvo que abras la conversación.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="md:col-span-3">
            <label className="text-xs text-gray-600">Cuenta</label>
            <select value={accountId} onChange={e => setAccountId(e.target.value)} className="w-full border rounded p-2 text-sm" data-testid="remote-cuenta">
              {accounts.length === 0 && <option value="">Sin cuentas IMAP</option>}
              {accounts.map(a => <option key={a.id} value={a.id}>{a.email}</option>)}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs text-gray-600">Buscar (asunto, remitente o cuerpo)</label>
            <input value={q} onChange={e => setQ(e.target.value)} className="w-full border rounded p-2 text-sm" placeholder='ej: "Elba Godoy"' data-testid="remote-query" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Desde</label>
            <input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="w-full border rounded p-2 text-sm" data-testid="remote-desde" />
          </div>
          <div>
            <label className="text-xs text-gray-600">Hasta</label>
            <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="w-full border rounded p-2 text-sm" data-testid="remote-hasta" />
          </div>
          <div className="flex items-end">
            <Button onClick={buscar} disabled={searching || !accountId} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="btn-remote-buscar">
              {searching ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Buscando…</> : <><Search className="w-4 h-4 mr-1" /> Buscar</>}
            </Button>
          </div>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {resultados.length > 0 && (
          <div className="mt-3 border rounded-lg divide-y" data-testid="remote-resultados">
            <p className="text-xs text-gray-500 px-3 py-1.5 bg-gray-50 border-b">{resultados.length} resultado{resultados.length !== 1 && 's'} (headers-only, en servidor)</p>
            {resultados.map((r, i) => (
              <div key={r.uid || i} className="p-3 hover:bg-gray-50 text-sm" data-testid={`remote-item-${i}`}>
                <div className="flex justify-between gap-2">
                  <p className="font-medium text-gray-900 truncate flex-1">{r.subject || '(sin asunto)'}</p>
                  <span className="text-xs text-gray-500 shrink-0">
                    {r.fecha ? new Date(r.fecha).toLocaleDateString('es-AR') : ''}
                  </span>
                </div>
                <p className="text-xs text-gray-600 truncate">{r.from}</p>
              </div>
            ))}
          </div>
        )}
        {!searching && !error && q && resultados.length === 0 && (
          <p className="text-sm text-gray-500 italic mt-3">Sin resultados para esa búsqueda.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CarpetaPersonalizadaDialog({ carpeta, etiquetas, onClose, onSaved }) {
  const [nombre, setNombre] = useState(carpeta?.nombre || '');
  const [color, setColor] = useState(carpeta?.color || '#0ea5e9');
  const [tagSel, setTagSel] = useState(carpeta?.etiquetas?.[0] || (etiquetas[0]?.id || ''));
  const [compartida, setCompartida] = useState(carpeta?.compartida || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const guardar = async () => {
    if (!nombre.trim()) { setError('Nombre obligatorio'); return; }
    if (!tagSel) { setError('Elegí una etiqueta'); return; }
    setSaving(true); setError('');
    try {
      const body = { nombre: nombre.trim(), etiquetas: [tagSel], color, compartida };
      if (carpeta?.id) {
        await axios.patch(`${API_URL}/bandeja-unificada/carpetas/${carpeta.id}`, body);
      } else {
        await axios.post(`${API_URL}/bandeja-unificada/carpetas`, body);
      }
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const eliminar = async () => {
    if (!carpeta?.id) return;
    if (!window.confirm('¿Eliminar esta carpeta?')) return;
    try {
      await axios.delete(`${API_URL}/bandeja-unificada/carpetas/${carpeta.id}`);
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="dialog-carpeta">
        <DialogHeader>
          <DialogTitle>{carpeta?.id ? 'Editar carpeta' : 'Nueva carpeta personalizada'}</DialogTitle>
          <DialogDescription>La carpeta actúa como filtro guardado por etiqueta.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nombre *</Label>
            <Input value={nombre} onChange={e => setNombre(e.target.value)} data-testid="input-carpeta-nombre" />
          </div>
          <div>
            <Label className="text-xs">Etiqueta *</Label>
            {etiquetas.length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Primero <Link to="/admin/bandeja-unificada/contactos-etiquetas" className="underline">creá una etiqueta</Link>.
              </p>
            ) : (
              <select className="w-full border rounded p-2 text-sm" value={tagSel} onChange={e => setTagSel(e.target.value)} data-testid="select-carpeta-etiqueta">
                {etiquetas.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
              </select>
            )}
          </div>
          <div>
            <Label className="text-xs">Color</Label>
            <div className="flex gap-2 flex-wrap">
              {['#7c3aed', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#64748b'].map(c => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 ${color === c ? 'border-gray-900' : 'border-gray-200'}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={compartida} onChange={e => setCompartida(e.target.checked)} data-testid="check-carpeta-compartida" />
            Compartir con todos los admins
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <DialogFooter className="flex justify-between">
          {carpeta?.id && (
            <Button variant="outline" onClick={eliminar} className="text-red-600" data-testid="btn-eliminar-carpeta">
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={guardar} disabled={saving || etiquetas.length === 0} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-guardar-carpeta">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Modal Asignar Etiquetas a una conversación ───
function AsignarEtiquetasDialog({ conv, etiquetas, onClose, onUpdated }) {
  const [asignadas, setAsignadas] = useState(new Set(conv?.etiquetas || []));
  const [saving, setSaving] = useState(false);

  const toggle = async (tag) => {
    setSaving(true);
    try {
      if (asignadas.has(tag.id)) {
        await axios.delete(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/etiquetas/${tag.id}`);
        const s = new Set(asignadas); s.delete(tag.id); setAsignadas(s);
      } else {
        await axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/etiquetas`, { etiqueta_id: tag.id });
        setAsignadas(new Set([...asignadas, tag.id]));
      }
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="dialog-asignar-etiquetas">
        <DialogHeader><DialogTitle>Etiquetas de la conversación</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {etiquetas.length === 0 ? (
            <p className="text-sm text-gray-500 italic">
              Todavía no creaste ninguna etiqueta. <Link to="/admin/bandeja-unificada/contactos-etiquetas" className="text-purple-600 underline">Crear una</Link>.
            </p>
          ) : etiquetas.map(t => {
            const on = asignadas.has(t.id);
            return (
              <button
                key={t.id}
                onClick={() => toggle(t)}
                disabled={saving}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border ${on ? 'ring-2 ring-purple-300' : 'hover:bg-gray-50'}`}
                style={{ background: on ? `${t.color}22` : 'white', borderColor: on ? t.color : '#e5e7eb' }}
                data-testid={`toggle-etiqueta-${t.id}`}
              >
                <Tag className="w-4 h-4 shrink-0" style={{ color: t.color }} />
                <span className="flex-1 text-left text-sm" style={{ color: on ? t.color : '#111827' }}>{t.nombre}</span>
                {on && <CheckCircle className="w-4 h-4" style={{ color: t.color }} />}
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button onClick={() => onUpdated(Array.from(asignadas))} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-cerrar-asignar-etiquetas">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Formulario Google OAuth ───
export function GoogleAddForm({ onDone }) {
  const [label, setLabel] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const iniciar = async () => {
    if (!label.trim()) { setError('Ponele un nombre a la cuenta (ej: Tesorería)'); return; }
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API_URL}/oauth/gmail/login?label=${encodeURIComponent(label)}&color=${encodeURIComponent(color)}`);
      window.location.href = r.data.authorization_url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al iniciar OAuth');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label>Nombre descriptivo *</Label>
        <Input placeholder="Ej: Tesorería" value={label} onChange={e => setLabel(e.target.value)} data-testid="input-google-label" />
      </div>
      <div>
        <Label>Color (para identificarla en la lista)</Label>
        <div className="flex gap-1.5 mt-1 flex-wrap">
          {COLORS.map(c => (
            <button key={c} type="button" className="w-7 h-7 rounded-full border-2" style={{ background: c, borderColor: color === c ? '#111' : 'transparent' }} onClick={() => setColor(c)} data-testid={`color-${c}`}></button>
          ))}
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>Cancelar</Button>
        <Button className="bg-purple-600 hover:bg-purple-700" onClick={iniciar} disabled={loading} data-testid="btn-iniciar-oauth-google">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
          Conectar con Google
        </Button>
      </DialogFooter>
    </div>
  );
}

// ─── Formulario IMAP ───
export function ImapAddForm({ onDone }) {
  const [form, setForm] = useState({
    label: '', email: '', password: '', host: 'imap.mail.yahoo.com', port: 993, color: COLORS[1],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const guardar = async () => {
    if (!form.label || !form.email || !form.password) { setError('Completá todos los campos obligatorios'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/bandeja-unificada/accounts/imap`, form);
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="p-2.5 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900">
        Para Yahoo generá una <strong>app-password</strong> en <a href="https://login.yahoo.com/account/security" target="_blank" rel="noopener noreferrer" className="underline">login.yahoo.com/account/security</a> (Generar contraseña de aplicación).
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><Label>Nombre descriptivo *</Label><Input placeholder="Ej: Recepción Yahoo" value={form.label} onChange={e => upd('label', e.target.value)} data-testid="input-imap-label" /></div>
        <div className="sm:col-span-2"><Label>Email *</Label><Input type="email" placeholder="cuenta@yahoo.com.ar" value={form.email} onChange={e => upd('email', e.target.value)} data-testid="input-imap-email" /></div>
        <div className="sm:col-span-2"><Label>App-password *</Label><Input type="password" placeholder="xxxx xxxx xxxx xxxx" value={form.password} onChange={e => upd('password', e.target.value.replace(/\s/g, ''))} data-testid="input-imap-password" /></div>
        <div><Label>Host IMAP</Label><Input value={form.host} onChange={e => upd('host', e.target.value)} data-testid="input-imap-host" /></div>
        <div><Label>Puerto</Label><Input type="number" value={form.port} onChange={e => upd('port', parseInt(e.target.value) || 993)} data-testid="input-imap-port" /></div>
        <div className="sm:col-span-2">
          <Label>Color</Label>
          <div className="flex gap-1.5 mt-1 flex-wrap">
            {COLORS.map(c => (
              <button key={c} type="button" className="w-7 h-7 rounded-full border-2" style={{ background: c, borderColor: form.color === c ? '#111' : 'transparent' }} onClick={() => upd('color', c)}></button>
            ))}
          </div>
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>Cancelar</Button>
        <Button className="bg-purple-600 hover:bg-purple-700" onClick={guardar} disabled={loading} data-testid="btn-guardar-imap">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
          Guardar cuenta
        </Button>
      </DialogFooter>
    </div>
  );
}

// ─── Drawer: detalle de conversación + panel CRM + notas + presencia ───
function ConversationDetailDrawer({ conv, onClose, onUpdate, onForward, etiquetasCatalogo = [], onAsignarEtiquetas }) {
  const [messages, setMessages] = useState([]);
  const [notas, setNotas] = useState([]);
  const [audit, setAudit] = useState([]);
  const [notasNueva, setNotasNueva] = useState('');
  const [savingNota, setSavingNota] = useState(false);
  const [tab, setTab] = useState('mensajes');
  const [showReply, setShowReply] = useState(false);
  const [firmaHtml, setFirmaHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [convDetail, setConvDetail] = useState(conv);
  const [admins, setAdmins] = useState([]);
  const [asignando, setAsignando] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);
  const [presenciaOtros, setPresenciaOtros] = useState([]);
  const [asignarOpen, setAsignarOpen] = useState(false);
  const [hidratando, setHidratando] = useState(0); // cantidad de mensajes pendientes de hidratar
  const [hidratarError, setHidratarError] = useState(null); // mensaje de error a mostrar
  const [reintentandoMsgId, setReintentandoMsgId] = useState(null); // id del mensaje siendo re-descargado manualmente
  const [ocultosCount, setOcultosCount] = useState(0);
  const [mostrarOcultos, setMostrarOcultos] = useState(false);
  const [ocultandoMsgId, setOcultandoMsgId] = useState(null);
  const onUpdateRef = useRef(onUpdate);
  const marcadoLeidoRef = useRef(false);
  const currentUserRef = useRef(null);
  const escribiendoRef = useRef(false);
  const escribiendoTimeoutRef = useRef(null);

  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);

  // Sincronizar cambios de etiquetas desde el padre (dialog de asignar etiquetas)
  useEffect(() => {
    setConvDetail(c => ({ ...c, etiquetas: conv.etiquetas || [] }));
  }, [conv.etiquetas]);

  // Carga inicial: mensajes + notas + admins + user info
  useEffect(() => {
    marcadoLeidoRef.current = false;
    let cancelado = false;
    (async () => {
      setLoading(true);
      try {
        const [m, n, a, u, au] = await Promise.all([
          axios.get(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/messages?incluir_ocultos=${mostrarOcultos}`),
          axios.get(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/notas`),
          axios.get(`${API_URL}/bandeja-unificada/admins-para-asignar`),
          axios.get(`${API_URL}/auth/me`).catch(() => null),
          axios.get(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/audit`).catch(() => ({ data: [] })),
        ]);
        if (cancelado) return;
        setMessages(m.data.messages || []);
        setOcultosCount(m.data.ocultos_count || 0);
        setConvDetail(m.data.conversation || conv);
        setNotas(n.data || []);
        setAdmins(a.data || []);
        setAudit(au.data || []);
        currentUserRef.current = u?.data || null;

        // Lazy-load: hidratar mensajes con cuerpo_pendiente en background.
        // Yahoo permite pocas sesiones concurrentes → estrictamente en SERIE,
        // uno por vez, con backoff. Si un mensaje sale por timeout, paramos
        // el loop para no saturar más el servidor: el usuario puede reintentar
        // el resto con el botón manual "Reintentar descarga" por mensaje.
        const pendientes = (m.data.messages || []).filter(x => x.cuerpo_pendiente);
        if (pendientes.length > 0) {
          setHidratando(pendientes.length);
          setHidratarError(null);
          (async () => {
            let quedan = pendientes.length;
            let hubotimeout = false;
            for (const msg of pendientes) {
              if (cancelado) return;
              let intentos = 0;
              let mensajeOk = false;
              while (intentos < 2) {
                try {
                  await axios.post(
                    `${API_URL}/bandeja-unificada/messages/${msg.id}/hidratar`,
                    null,
                    { timeout: 35000 },
                  );
                  mensajeOk = true;
                  break;
                } catch (err) {
                  const detail = err.response?.data?.detail || err.message || '';
                  const esTimeout = err.code === 'ECONNABORTED'
                    || /timeout|Servidor ocupado/i.test(detail);
                  intentos += 1;
                  if (esTimeout && intentos < 2) {
                    hubotimeout = true;
                    setHidratarError('Servidor ocupado, reintentando…');
                    await new Promise(r => setTimeout(r, 4000));
                    continue;
                  }
                  if (esTimeout) {
                    setHidratarError('Servidor de Yahoo ocupado. Los mensajes restantes pueden re-descargarse manualmente.');
                  }
                  break;
                }
              }
              quedan -= 1;
              if (!cancelado) setHidratando(quedan);
              // Si este mensaje falló por timeout, cortamos el loop para no
              // seguir saturando IMAP. Los restantes quedan como "pendientes"
              // y el usuario puede re-lanzarlos con "Reintentar descarga".
              if (!mensajeOk && hubotimeout) {
                if (!cancelado) setHidratando(0);
                break;
              }
              // Throttle entre requests: 800ms para dar aire a IMAP.
              if (!cancelado) await new Promise(r => setTimeout(r, 800));
            }
            if (cancelado) return;
            try {
              const rr = await axios.get(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/messages?incluir_ocultos=${mostrarOcultos}`);
              if (!cancelado) {
                setMessages(rr.data.messages || []);
                setOcultosCount(rr.data.ocultos_count || 0);
              }
              if (!cancelado && !hubotimeout) setHidratarError(null);
            } catch (_) { /* silent */ }
          })();
        }
        // Cargar firma de la cuenta para preview del composer
        try {
          const accId = (m.data.conversation || conv).account_id;
          if (accId) {
            const fr = await axios.get(`${API_URL}/bandeja-unificada/accounts/${accId}/firma`);
            if (!cancelado) setFirmaHtml(fr.data?.firma_html || '');
          }
        } catch (e) { /* silent */ }
        if (!conv.leido && !marcadoLeidoRef.current) {
          marcadoLeidoRef.current = true;
          await axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/leido`, { leido: true });
          onUpdateRef.current?.();
        }
      } catch (e) { console.error(e); }
      if (!cancelado) setLoading(false);
    })();
    return () => { cancelado = true; };
  }, [conv.thread_id, conv.leido, mostrarOcultos]);

  // Heartbeat presencia + polling de otros presentes (cada 15s).
  useEffect(() => {
    let interval;
    const hb = async () => {
      try {
        await axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/presencia`, { escribiendo: escribiendoRef.current });
        const r = await axios.get(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/presencia`);
        setPresenciaOtros(r.data || []);
      } catch (e) { /* silencioso */ }
    };
    hb();
    interval = setInterval(hb, 15000);
    return () => {
      clearInterval(interval);
      axios.delete(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/presencia`).catch(() => {});
    };
  }, [conv.thread_id]);

  // Re-descarga manual del cuerpo de un mensaje que quedó marcado con
  // `body_hidratacion_fallida`. Fuerza al backend a re-intentar todas las
  // estrategias (RFC822 / BODY.PEEK[] / BODY.PEEK[TEXT]).
  const reintentarHidratacion = async (msg) => {
    if (!msg?.id) return;
    setReintentandoMsgId(msg.id);
    try {
      await axios.post(
        `${API_URL}/bandeja-unificada/messages/${msg.id}/hidratar?force=true`,
        null,
        { timeout: 40000 },
      );
      // Refrescar la lista de mensajes tras la re-descarga.
      const rr = await axios.get(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/messages?incluir_ocultos=${mostrarOcultos}`);
      setMessages(rr.data.messages || []);
      setOcultosCount(rr.data.ocultos_count || 0);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'No se pudo re-descargar el cuerpo';
      setHidratarError(detail);
    } finally {
      setReintentandoMsgId(null);
    }
  };

  // Ocultar / mostrar un mensaje específico de la cronología del hilo.
  // Es reversible y usa un flag `oculto=true` en el documento.
  const toggleOcultarMensaje = async (msg) => {
    if (!msg?.id) return;
    const yaOculto = !!msg.oculto;
    setOcultandoMsgId(msg.id);
    try {
      const path = yaOculto ? 'mostrar' : 'ocultar';
      await axios.post(`${API_URL}/bandeja-unificada/messages/${msg.id}/${path}`);
      const rr = await axios.get(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/messages?incluir_ocultos=${mostrarOcultos}`);
      setMessages(rr.data.messages || []);
      setOcultosCount(rr.data.ocultos_count || 0);
    } catch (err) {
      setHidratarError(err.response?.data?.detail || 'No se pudo cambiar la visibilidad del mensaje');
    } finally {
      setOcultandoMsgId(null);
    }
  };

  // Marca "escribiendo=true" y programa el auto-reset después de 8s sin tipear.
  // Envía un heartbeat inmediato para que otros sepan enseguida.
  const marcarEscribiendo = () => {
    if (!escribiendoRef.current) {
      escribiendoRef.current = true;
      axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/presencia`, { escribiendo: true }).catch(() => {});
    }
    if (escribiendoTimeoutRef.current) clearTimeout(escribiendoTimeoutRef.current);
    escribiendoTimeoutRef.current = setTimeout(() => {
      escribiendoRef.current = false;
      axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/presencia`, { escribiendo: false }).catch(() => {});
    }, 8000);
  };

  const refrescar = async () => {
    try {
      const [r, au] = await Promise.all([
        axios.get(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/messages`),
        axios.get(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/audit`).catch(() => ({ data: [] })),
      ]);
      setConvDetail(r.data.conversation);
      setMessages(r.data.messages || []);
      setAudit(au.data || []);
      onUpdateRef.current?.();
    } catch (e) { console.error(e); }
  };

  const cambiarEstado = async (nuevo) => {
    setCambiandoEstado(true);
    try {
      await axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/estado`, { estado: nuevo });
      setConvDetail(c => ({ ...c, estado: nuevo }));
      onUpdateRef.current?.();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setCambiandoEstado(false);
  };

  const asignarme = async () => {
    setAsignando(true);
    try {
      const r = await axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/asignar`, {});
      setConvDetail(c => ({ ...c, responsable_id: r.data.responsable.id || r.data.responsable.email, responsable_snapshot: r.data.responsable }));
      onUpdateRef.current?.();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setAsignando(false);
  };

  const asignarA = async (adminUser) => {
    setAsignando(true);
    try {
      const r = await axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/asignar`, { responsable_id: adminUser.id || adminUser.email });
      setConvDetail(c => ({ ...c, responsable_id: r.data.responsable.id || r.data.responsable.email, responsable_snapshot: r.data.responsable }));
      setAsignarOpen(false);
      onUpdateRef.current?.();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setAsignando(false);
  };

  const desasignar = async () => {
    if (!window.confirm('¿Quitar el responsable de esta conversación?')) return;
    try {
      await axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/desasignar`);
      setConvDetail(c => ({ ...c, responsable_id: null, responsable_snapshot: null }));
      onUpdateRef.current?.();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const guardarNota = async (contenido, menciones = [], notificarPorEmail = false) => {
    const txt = (contenido || '').trim();
    if (!txt) return;
    setSavingNota(true);
    try {
      const r = await axios.post(
        `${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/notas`,
        { contenido: txt, menciones, notificar_por_email: notificarPorEmail },
      );
      setNotas(n => [...n, r.data]);
      setNotasNueva('');
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setSavingNota(false);
  };

  const borrarNota = async (nota) => {
    if (!window.confirm('¿Borrar esta nota interna?')) return;
    try {
      await axios.delete(`${API_URL}/bandeja-unificada/notas/${nota.id}`);
      setNotas(n => n.filter(x => x.id !== nota.id));
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const yoSoyResponsable = convDetail.responsable_id && currentUserRef.current &&
    (convDetail.responsable_id === currentUserRef.current.id || convDetail.responsable_id === currentUserRef.current.email);

  const escribiendo = presenciaOtros.find(p => p.escribiendo);
  const abriendo = presenciaOtros.filter(p => !p.escribiendo);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4" data-testid="conv-detail-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full h-full max-w-none sm:max-w-6xl sm:h-auto sm:max-h-[92vh] bg-white shadow-2xl sm:rounded-lg flex flex-col md:flex-row overflow-y-auto md:overflow-hidden" data-testid="conv-drawer-inner">
        {/* Columna principal */}
        <div className="flex flex-col min-w-0 flex-shrink-0 md:flex-shrink md:min-h-0 md:flex-1 md:overflow-hidden md:border-r">
          {/* Top bar: asunto + estado + asignar */}
          <div className="p-4 border-b space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ background: convDetail.account_color || '#7c3aed' }}></span>
                  {convDetail.account_label}
                </p>
                <h2 className="text-lg font-semibold text-gray-900 truncate">{convDetail.asunto || '(sin asunto)'}</h2>
                <p className="text-sm text-gray-600 truncate">{convDetail.remitente}</p>
              </div>
              <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="btn-close-drawer"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={convDetail.estado || 'nuevo'}
                onChange={e => cambiarEstado(e.target.value)}
                disabled={cambiandoEstado}
                className={`text-xs px-2 py-1 rounded border font-medium ${estadoColor(convDetail.estado)}`}
                data-testid="select-estado"
              >
                {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
              {convDetail.responsable_id ? (
                <div className="flex items-center gap-1 text-xs bg-indigo-50 px-2 py-1 rounded" data-testid="chip-responsable">
                  <UserCircle className="w-3.5 h-3.5 text-indigo-600" />
                  <span className="font-medium text-indigo-800">{convDetail.responsable_snapshot?.nombre || convDetail.responsable_snapshot?.email || convDetail.responsable_id}</span>
                  <button onClick={desasignar} className="ml-1 text-indigo-400 hover:text-indigo-700" title="Desasignar" data-testid="btn-desasignar">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <Button size="sm" onClick={asignarme} disabled={asignando} className="h-7 text-xs bg-purple-600 hover:bg-purple-700" data-testid="btn-asignarme">
                  {asignando ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCircle className="w-3 h-3 mr-1" />}
                  Asignarme
                </Button>
              )}
              <div className="relative">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAsignarOpen(o => !o)} data-testid="btn-reasignar">
                  <Users className="w-3 h-3 mr-1" /> {yoSoyResponsable ? 'Reasignar' : 'Asignar a…'}
                </Button>
                {asignarOpen && (
                  <div className="absolute z-10 top-full mt-1 right-0 bg-white border rounded-lg shadow-lg w-56 max-h-64 overflow-y-auto" data-testid="asignar-dropdown">
                    {admins.length === 0 ? (
                      <p className="text-xs text-gray-500 italic p-2">Sin admins disponibles</p>
                    ) : admins.map(a => (
                      <button
                        key={a.id || a.email}
                        onClick={() => asignarA(a)}
                        className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm border-b last:border-b-0"
                        data-testid={`asignar-a-${a.email}`}
                      >
                        <p className="font-medium truncate">{a.nombre?.trim() || a.email}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {a.nombre?.trim() ? `${a.email} · ${a.rol}` : a.rol}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Chips de etiquetas + botón para editar */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => onAsignarEtiquetas?.(convDetail)}
                className="text-xs px-2 py-1 rounded border border-dashed border-gray-300 hover:border-purple-400 hover:text-purple-700 text-gray-500 inline-flex items-center gap-1"
                data-testid="btn-editar-etiquetas-drawer"
              >
                <Tag className="w-3 h-3" /> Etiquetas
              </button>
              {(convDetail.etiquetas || []).map(tid => {
                const t = etiquetasCatalogo.find(x => x.id === tid);
                if (!t) return null;
                return (
                  <span
                    key={tid}
                    className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    style={{ background: `${t.color}22`, color: t.color }}
                    data-testid={`drawer-etiqueta-${tid}`}
                  >
                    <Tag className="w-3 h-3" />{t.nombre}
                  </span>
                );
              })}
            </div>

            {/* Presencia banner */}
            {(escribiendo || abriendo.length > 0) && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-800 flex items-center gap-1.5" data-testid="presence-banner">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                {escribiendo
                  ? `${escribiendo.admin_nombre} está redactando una respuesta…`
                  : `${abriendo.map(o => o.admin_nombre).join(', ')} ${abriendo.length === 1 ? 'tiene' : 'tienen'} abierta esta conversación`}
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="border-b flex items-center gap-1 px-2 sm:px-4 overflow-x-auto flex-nowrap">
            <button
              className={`px-2 sm:px-3 py-2 text-xs sm:text-sm border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${tab === 'mensajes' ? 'border-purple-600 font-semibold text-purple-700' : 'border-transparent text-gray-600'}`}
              onClick={() => setTab('mensajes')}
              data-testid="tab-mensajes"
            >
              <MessageSquare className="w-4 h-4 inline mr-1" /> <span className="hidden sm:inline">Mensajes </span>({messages.length})
            </button>
            <button
              className={`px-2 sm:px-3 py-2 text-xs sm:text-sm border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${tab === 'notas' ? 'border-amber-500 font-semibold text-amber-700' : 'border-transparent text-gray-600'}`}
              onClick={() => setTab('notas')}
              data-testid="tab-notas"
            >
              <StickyNote className="w-4 h-4 inline mr-1" /> <span className="hidden sm:inline">Notas </span>({notas.length})
            </button>
            <button
              className={`px-2 sm:px-3 py-2 text-xs sm:text-sm border-b-2 -mb-px whitespace-nowrap flex-shrink-0 ${tab === 'auditoria' ? 'border-slate-600 font-semibold text-slate-700' : 'border-transparent text-gray-600'}`}
              onClick={() => setTab('auditoria')}
              data-testid="tab-auditoria"
            >
              <History className="w-4 h-4 inline mr-1" /> <span className="hidden sm:inline">Auditoría </span>({audit.length})
            </button>
            <div className="flex-1" />
            {!showReply && (
              <button
                onClick={() => setShowReply(true)}
                className="my-1 px-2 sm:px-3 py-1 rounded bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm inline-flex items-center gap-1 flex-shrink-0"
                data-testid="btn-open-reply"
              >
                <Reply className="w-4 h-4" /> <span className="hidden sm:inline">Responder</span>
              </button>
            )}
            <button
              onClick={() => {
                const ultimo = messages[messages.length - 1] || {};
                const fecha = ultimo.fecha ? new Date(ultimo.fecha).toLocaleString('es-AR') : '';
                const cita = `<br><br><div style="border-left:3px solid #ccc;padding-left:10px;color:#555;font-size:13px">
<p><b>---------- Mensaje reenviado ----------</b></p>
<p><b>De:</b> ${ultimo.from || ''}<br>
<b>Fecha:</b> ${fecha}<br>
<b>Asunto:</b> ${ultimo.subject || convDetail.asunto || ''}<br>
<b>Para:</b> ${ultimo.to || ''}</p>
${ultimo.body_html || ultimo.body_text || ''}
</div>`;
                const s = (ultimo.subject || convDetail.asunto || '').replace(/^(Fwd:|RV:|Reenviado:)\s*/i, '');
                onForward?.({
                  subject: `Fwd: ${s}`,
                  body_html: cita,
                  account_id: convDetail.account_id,
                });
              }}
              className="my-1 px-2 sm:px-3 py-1 rounded border border-purple-300 text-purple-700 hover:bg-purple-50 text-xs sm:text-sm inline-flex items-center gap-1 flex-shrink-0"
              data-testid="btn-forward"
              title="Reenviar"
            >
              <Forward className="w-4 h-4" /> <span className="hidden sm:inline">Reenviar</span>
            </button>
          </div>

          {/* Contenido tab */}
          <div className="md:flex-1 md:overflow-y-auto p-4 space-y-4 min-h-[240px]">
            {loading ? (
              <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-purple-600 mx-auto" /></div>
            ) : tab === 'mensajes' ? (
              <>
                {(hidratando > 0 || hidratarError) && (
                  <div
                    className={`mb-3 flex items-center gap-2 rounded px-3 py-2 text-xs border ${
                      hidratarError
                        ? 'bg-amber-50 border-amber-300 text-amber-800'
                        : 'bg-blue-50 border-blue-200 text-blue-800'
                    }`}
                    data-testid="banner-hidratacion"
                  >
                    <Loader2 className={`w-3.5 h-3.5 shrink-0 ${hidratarError ? '' : 'animate-spin'}`} />
                    <span className="flex-1">
                      {hidratarError
                        ? hidratarError
                        : `Descargando contenido de ${hidratando} mensaje${hidratando === 1 ? '' : 's'} del servidor…`}
                    </span>
                  </div>
                )}
                {ocultosCount > 0 && (
                  <div
                    className="mb-3 flex items-center gap-2 rounded bg-gray-100 border border-gray-200 px-3 py-1.5 text-xs text-gray-600"
                    data-testid="banner-ocultos"
                  >
                    <EyeOff className="w-3.5 h-3.5" />
                    <span className="flex-1">
                      <strong>{ocultosCount}</strong> mensaje{ocultosCount === 1 ? '' : 's'} oculto{ocultosCount === 1 ? '' : 's'} en este hilo
                    </span>
                    <button
                      onClick={() => setMostrarOcultos(v => !v)}
                      className="text-purple-700 hover:underline"
                      data-testid="btn-toggle-ocultos"
                    >
                      {mostrarOcultos ? 'Volver a ocultarlos' : 'Mostrarlos'}
                    </button>
                  </div>
                )}
                {messages.length === 0 ? (
                  <p className="text-sm text-gray-500 italic text-center py-8">Sin mensajes</p>
                ) : (
                  messages.map((m, idx) => (
                    <div key={idx} className={`border rounded-lg p-3 mb-3 ${m.enviado_por_nosotros ? 'bg-purple-50/60 border-purple-200' : 'bg-gray-50'} ${m.cuerpo_pendiente ? 'opacity-70' : ''} ${m.oculto ? 'opacity-50 border-dashed' : ''}`} data-testid={`msg-${idx}`}>
                      {m.cuerpo_pendiente && (
                        <p className="text-xs text-amber-700 mb-1 italic flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> Cuerpo pendiente de descargar…
                        </p>
                      )}
                      {m.oculto && (
                        <p className="text-xs text-gray-500 mb-1 italic flex items-center gap-1">
                          <EyeOff className="w-3 h-3" /> Mensaje oculto de la cronología
                        </p>
                      )}
                      <div className="flex justify-between items-baseline gap-3 mb-2 text-xs">
                        <span className="font-medium text-gray-800 truncate">
                          {m.enviado_por_nosotros && <span className="text-purple-700 mr-1">↳</span>}
                        {m.from}
                      </span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-gray-500">{m.fecha}</span>
                        <button
                          onClick={() => toggleOcultarMensaje(m)}
                          disabled={ocultandoMsgId === m.id}
                          className="text-gray-400 hover:text-gray-700 disabled:opacity-40"
                          title={m.oculto ? 'Mostrar este mensaje en la cronología' : 'Ocultar este mensaje de la cronología'}
                          data-testid={`btn-ocultar-mensaje-${idx}`}
                        >
                          {ocultandoMsgId === m.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : m.oculto ? (
                            <Eye className="w-3.5 h-3.5" />
                          ) : (
                            <EyeOff className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                    {m.body_html ? (
                      <div className="text-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: m.body_html }} />
                    ) : m.body_text ? (
                      <pre className="text-sm whitespace-pre-wrap font-sans">{m.body_text}</pre>
                    ) : (
                      <>
                        {/* Snippet siempre visible como pista para la administrativa */}
                        {m.snippet && (
                          <p className="text-sm text-gray-700 italic mb-2" data-testid={`msg-snippet-${idx}`}>
                            <span className="text-xs font-semibold text-gray-500 not-italic">Resumen: </span>
                            {m.snippet}
                          </p>
                        )}
                        {/* Warning: body no descargable — snippet y adjuntos aún se muestran */}
                        {m.body_hidratacion_fallida && (
                          <div
                            className="mt-1 border border-amber-300 bg-amber-50 rounded-md p-3 text-sm text-amber-900"
                            data-testid={`msg-body-fallido-${idx}`}
                          >
                            <p className="font-medium text-amber-900 flex items-start gap-2">
                              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              <span>
                                El servidor de Yahoo no devolvió el contenido de este mensaje.
                                Podés intentar re-descargarlo o ver los adjuntos abajo.
                              </span>
                            </p>
                            <div className="mt-2 flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => reintentarHidratacion(m)}
                                disabled={reintentandoMsgId === m.id}
                                className="border-amber-400 text-amber-800 hover:bg-amber-100"
                                data-testid={`btn-reintentar-descarga-${idx}`}
                              >
                                {reintentandoMsgId === m.id ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Re-descargando…</>
                                ) : (
                                  <><RefreshCw className="w-3 h-3 mr-1" /> Reintentar descarga</>
                                )}
                              </Button>
                            </div>
                          </div>
                        )}
                        {/* Fallback: si no hay snippet ni marca de falla, mostramos placeholder mínimo */}
                        {!m.snippet && !m.body_hidratacion_fallida && (
                          <p className="text-sm text-gray-400 italic">(sin contenido)</p>
                        )}
                      </>
                    )}
                    {(() => {
                      // Attachments unificados: entrantes usan m.attachments con storage_key,
                      // salientes usan m.adjuntos (sin storage_key). Diferimos endpoint.
                      const entrantes = (m.attachments || []).map(a => ({
                        ...a,
                        kind: 'entrante',
                        msgId: m.message_id || '',
                        content_type: a.mime,
                      }));
                      const salientes = (m.adjuntos || []).map(a => ({
                        ...a,
                        kind: 'saliente',
                      }));
                      // Inline images (fotos desde móvil, etc.): endpoint público
                      // sin signed URL (basado en UUID impredecible).
                      const backend = process.env.REACT_APP_BACKEND_URL || '';
                      const inline = (m.inline_images || []).map(a => ({
                        ...a,
                        kind: 'inline',
                        content_type: a.mime,
                        directUrl: `${backend}/api/bandeja-unificada/messages/${encodeURIComponent(m.message_id || '')}/inline/${a.id}`,
                      }));
                      const todos = [...entrantes, ...salientes, ...inline];
                      if (!todos.length) return null;
                      const fmtSize = (b) => {
                        if (!b) return '';
                        if (b < 1024) return `${b} B`;
                        if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
                        return `${(b / (1024 * 1024)).toFixed(1)} MB`;
                      };
                      const abrirAdjunto = async (a) => {
                        try {
                          if (a.kind === 'inline') {
                            window.open(a.directUrl, '_blank', 'noopener,noreferrer');
                            return;
                          }
                          const endpoint = a.kind === 'entrante'
                            ? `${API_URL}/bandeja-unificada/messages/${encodeURIComponent(a.msgId)}/attachments/${a.id}/signed-url`
                            : `${API_URL}/bandeja-unificada/attachments/${a.id}/signed-url`;
                          const r = await axios.post(endpoint);
                          const abs = r.data?.url?.startsWith('http') ? r.data.url : backend + r.data.url;
                          window.open(abs, '_blank', 'noopener,noreferrer');
                        } catch (e) {
                          alert(e.response?.data?.detail || 'No se pudo abrir el adjunto');
                        }
                      };
                      const iconoPorKind = (a) => {
                        if (a.kind === 'inline' || (a.content_type || '').startsWith('image/')) return '🖼️';
                        if ((a.content_type || '').includes('pdf')) return '📄';
                        return '📎';
                      };
                      return (
                        <div className="mt-2 pt-2 border-t flex flex-wrap gap-1.5" data-testid={`msg-adjuntos-${idx}`}>
                          {todos.map(a => (
                            <button
                              key={`${a.kind}-${a.id}`}
                              type="button"
                              onClick={() => abrirAdjunto(a)}
                              className="text-xs inline-flex items-center gap-1 bg-white border rounded px-2 py-1 hover:bg-purple-50"
                              title={`${a.filename}${a.kind === 'inline' ? ' (imagen incrustada)' : ''} · ${fmtSize(a.size)}${a.content_type ? ` · ${a.content_type}` : ''}`}
                              data-testid={`att-link-${a.id}`}
                            >
                              {iconoPorKind(a)} <span className="truncate max-w-[180px]">{a.filename}</span>
                              {a.size > 0 && <span className="text-gray-400 ml-1">{fmtSize(a.size)}</span>}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                ))
                )}
              </>
            ) : tab === 'auditoria' ? (
              <div className="space-y-2" data-testid="pane-auditoria">
                {audit.length === 0 ? (
                  <p className="text-sm text-gray-500 italic text-center py-4">Sin actividad registrada.</p>
                ) : audit.map(a => (
                  <div key={a.id} className="flex items-start gap-2 text-xs border-l-2 border-slate-300 pl-3 py-1" data-testid={`audit-${a.id}`}>
                    <div className="flex-1">
                      <p className="text-gray-800">
                        <span className="font-medium text-slate-700">{a.usuario_nombre || a.usuario_email}</span>
                        {' '}
                        <span className="text-gray-600">{a.descripcion}</span>
                        {a.metadata?.estado && <span className="ml-1 px-1.5 rounded bg-slate-100">{a.metadata.estado}</span>}
                        {a.metadata?.responsable_nombre && <span className="ml-1 text-purple-700">→ {a.metadata.responsable_nombre}</span>}
                        {a.metadata?.matriculado && <span className="ml-1 text-emerald-700">→ {a.metadata.matriculado}</span>}
                      </p>
                      <p className="text-gray-500">
                        {a.created_at ? new Date(a.created_at).toLocaleString('es-AR') : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Tab Notas internas
              <div className="space-y-3" data-testid="pane-notas">
                {notas.length === 0 ? (
                  <p className="text-sm text-gray-500 italic text-center py-4">Sin notas internas todavía.</p>
                ) : notas.map(n => (
                  <div key={n.id} className="bg-amber-50 border-l-4 border-amber-400 rounded p-3" data-testid={`nota-${n.id}`}>
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="text-xs font-semibold text-amber-900">{n.autor_nombre || n.autor_email}</span>
                      <span className="text-xs text-amber-700">{n.created_at ? new Date(n.created_at).toLocaleString('es-AR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : ''}</span>
                    </div>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {(n.contenido || '').split(/(@[\w.\-]+)/g).map((part, i) =>
                        /^@[\w.\-]+$/.test(part) ? (
                          <span key={i} className="bg-purple-200 text-purple-900 rounded px-1 font-medium">{part}</span>
                        ) : <React.Fragment key={i}>{part}</React.Fragment>
                      )}
                    </p>
                    <button onClick={() => borrarNota(n)} className="mt-1 text-[10px] text-red-600 hover:underline" data-testid={`btn-borrar-nota-${n.id}`}>Borrar</button>
                  </div>
                ))}
                <div className="border-t pt-3">
                  <NotaMencionesInput
                    onSubmit={guardarNota}
                    saving={savingNota}
                    onTyping={marcarEscribiendo}
                    placeholder="Escribí una nota interna. Usá @ para mencionar a un admin."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Reply Composer */}
          {showReply && (
            <ReplyComposer
              conv={convDetail}
              ultimoMsg={messages[messages.length - 1]}
              accountFirmaHtml={firmaHtml}
              onSent={() => { setShowReply(false); refrescar(); setTab('mensajes'); }}
              onCancel={() => setShowReply(false)}
              onTyping={marcarEscribiendo}
            />
          )}
        </div>
        {/* Columna panel matriculado */}
        <MatriculadoContextPanel conv={convDetail} onRefresh={refrescar} />
      </div>
    </div>
  );
}

// ─── Panel lateral: ficha del matriculado o buscador manual ───
function MatriculadoContextPanel({ conv, onRefresh }) {
  const [ficha, setFicha] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loadingFicha, setLoadingFicha] = useState(false);
  const [actualizandoDeuda, setActualizandoDeuda] = useState(false);
  const [errorDeuda, setErrorDeuda] = useState(null);
  const [contactoExterno, setContactoExterno] = useState(null);
  const [loadingContacto, setLoadingContacto] = useState(false);

  const actualizarDeuda = async () => {
    if (!conv.trabajador_id) return;
    setActualizandoDeuda(true);
    setErrorDeuda(null);
    try {
      const r = await axios.post(
        `${API_URL}/bandeja-unificada/matriculados/${conv.trabajador_id}/actualizar-deuda`,
        null,
        { timeout: 60000 },
      );
      setFicha(prev => prev ? { ...prev, deuda: r.data.deuda } : prev);
    } catch (e) {
      setErrorDeuda(e.response?.data?.detail || 'Error al consultar ANTUAR');
    } finally {
      setActualizandoDeuda(false);
    }
  };

  const formatoFechaActualizacion = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  useEffect(() => {
    (async () => {
      if (conv.trabajador_id) {
        setLoadingFicha(true);
        try {
          const [f, h] = await Promise.all([
            axios.get(`${API_URL}/bandeja-unificada/matriculados/${conv.trabajador_id}/ficha`),
            axios.get(`${API_URL}/bandeja-unificada/matriculados/${conv.trabajador_id}/conversaciones-previas?conv_actual=${encodeURIComponent(conv.thread_id)}&limit=4`),
          ]);
          setFicha(f.data);
          setHistorial(h.data || []);
        } catch (e) { console.error(e); }
        setLoadingFicha(false);
        setContactoExterno(null);
      } else {
        setFicha(null); setHistorial([]);
        // Buscar contacto externo por email del remitente
        const email = extractEmail(conv.remitente || conv.counterparty_email || '');
        if (email) {
          setLoadingContacto(true);
          try {
            const r = await axios.get(`${API_URL}/bandeja-unificada/contactos-externos/por-email?email=${encodeURIComponent(email)}`);
            setContactoExterno(r.data);
          } catch (e) { setContactoExterno(null); }
          setLoadingContacto(false);
        } else {
          setContactoExterno(null);
        }
      }
    })();
  }, [conv.trabajador_id, conv.thread_id, conv.remitente, conv.counterparty_email]);

  const desvincular = async () => {
    if (!window.confirm('¿Desvincular este matriculado de la conversación?')) return;
    try {
      await axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/desvincular`);
      onRefresh?.();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  return (
    <aside className="w-full md:w-80 bg-slate-50 flex flex-col flex-shrink-0 md:flex-shrink md:overflow-hidden border-t md:border-t-0" data-testid="panel-matriculado">
      <div className="p-4 border-b bg-white">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Info del remitente</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {conv.trabajador_id && ficha?.trabajador ? (
          <>
            {/* Ficha */}
            <div className="bg-white rounded-lg border p-3 space-y-2" data-testid="ficha-matriculado">
              <div className="flex items-center gap-2">
                <UserCircle className="w-8 h-8 text-emerald-600" />
                <div>
                  <p className="font-semibold text-gray-900 leading-tight">{ficha.trabajador.apellido}, {ficha.trabajador.nombre}</p>
                  <p className="text-xs text-gray-500">Matriculado identificado</p>
                </div>
              </div>
              <div className="text-sm space-y-1 pt-1 border-t">
                <p><span className="text-gray-500">Matrícula:</span> <span className="font-medium">{ficha.trabajador.numero_matricula || '—'}</span></p>
                <p><span className="text-gray-500">DNI:</span> <span className="font-medium">{ficha.trabajador.documento || '—'}</span></p>
                <p><span className="text-gray-500">Email:</span> <span className="font-medium truncate block">{ficha.trabajador.email || '—'}</span></p>
                {ficha.trabajador.celular && <p><span className="text-gray-500">Celular:</span> <span className="font-medium">{ficha.trabajador.celular}</span></p>}
                {conv.vinculacion_manual && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-2">Vinculación manual</p>
                )}
              </div>
              <div className="pt-2 flex gap-2">
                <a
                  href={`/admin/dashboard?matricula=${encodeURIComponent(ficha.trabajador.numero_matricula || '')}&ver=1`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1 text-xs px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700"
                  data-testid="btn-ver-ficha-completa"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Ver ficha completa
                </a>
                <button
                  onClick={desvincular}
                  className="text-xs px-2 py-1.5 border rounded text-red-600 hover:bg-red-50 flex items-center gap-1"
                  title="Desvincular"
                  data-testid="btn-desvincular"
                >
                  <Unlink className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Estado */}
            <div className="bg-white rounded-lg border p-3 text-sm space-y-1" data-testid="estado-institucional">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Estado</p>
              <p><span className="text-gray-500">Matrícula:</span>{' '}
                <span className={`font-medium ${(ficha.trabajador.activa !== false) ? 'text-green-700' : 'text-red-700'}`}>
                  {(ficha.trabajador.activa !== false) ? 'Activa' : 'Inactiva'}
                </span>
              </p>
              {ficha.deuda ? (
                <>
                  {(() => {
                    const vencida = ficha.deuda.total_deuda_vencida || 0;
                    const cuotasV = ficha.deuda.cuotas_vencidas || 0;
                    const alDia = vencida <= 0 && cuotasV === 0;
                    return (
                      <>
                        <p>
                          <span className="text-gray-500">Situación:</span>{' '}
                          <span className={`font-semibold ${alDia ? 'text-green-700' : 'text-red-700'}`} data-testid="deuda-situacion">
                            {alDia ? 'Al día' : 'Con deuda vencida'}
                          </span>
                        </p>
                        {!alDia && (
                          <>
                            <p><span className="text-gray-500">Deuda vencida:</span>{' '}
                              <span className="font-medium text-red-700" data-testid="deuda-monto">
                                ${vencida.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </p>
                            {cuotasV > 0 && (
                              <p><span className="text-gray-500">Cuotas vencidas:</span> <span className="font-medium">{cuotasV}</span></p>
                            )}
                          </>
                        )}
                        <p>
                          <span className="text-gray-500">Plan de pago:</span>{' '}
                          <span className={`font-medium ${ficha.deuda.tiene_plan_pago ? 'text-amber-700' : 'text-gray-700'}`} data-testid="deuda-plan-pago">
                            {ficha.deuda.tiene_plan_pago ? 'Sí (activo)' : 'No'}
                          </span>
                        </p>
                        {ficha.deuda.mes_cache && (
                          <p className="text-[11px] text-gray-400 pt-1">
                            Datos del ciclo {ficha.deuda.mes_cache}
                          </p>
                        )}
                        {ficha.deuda.actualizado_at && (
                          <p className="text-[11px] text-gray-500 pt-0.5" data-testid="deuda-actualizado-at">
                            Actualizado al {formatoFechaActualizacion(ficha.deuda.actualizado_at)}
                            {ficha.deuda.actualizado_on_demand && (
                              <span className="ml-1 text-emerald-600">· on-demand</span>
                            )}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <p className="text-xs text-gray-500 italic">Sin consulta reciente en caché mensual.</p>
              )}
              {/* Botón on-demand: solo si tiene usuario creado (o sea, tenemos los datos que
                  necesita ANTUAR) y no hay cache reciente todavía. Si ya hay cache, el link
                  "Consultar deuda actualizada" sigue abajo para consulta manual. */}
              {ficha.trabajador.tiene_usuario && !ficha.deuda && (
                <button
                  type="button"
                  onClick={actualizarDeuda}
                  disabled={actualizandoDeuda}
                  className="mt-2 inline-flex items-center justify-center gap-1 text-xs px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed w-full"
                  data-testid="btn-actualizar-deuda"
                >
                  {actualizandoDeuda ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Consultando ANTUAR...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3.5 h-3.5" />
                      Actualizar Situación de Deuda
                    </>
                  )}
                </button>
              )}
              {errorDeuda && (
                <p className="text-[11px] text-red-600 pt-1" data-testid="deuda-error">{errorDeuda}</p>
              )}
              <a
                href={`/admin/consulta-deuda?matricula=${encodeURIComponent(ficha.trabajador.numero_matricula || '')}&documento=${encodeURIComponent(ficha.trabajador.documento || '')}${ficha.trabajador.fecha_nacimiento ? `&fecha_nacimiento=${encodeURIComponent(ficha.trabajador.fecha_nacimiento)}` : ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-1 text-xs px-3 py-1.5 border border-purple-300 text-purple-700 rounded hover:bg-purple-50 w-full"
                data-testid="btn-consultar-deuda-actualizada"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Consultar deuda actualizada
              </a>
            </div>

            {/* Historial */}
            <div className="bg-white rounded-lg border p-3" data-testid="historial-conversaciones">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Últimas conversaciones</p>
              {historial.length === 0 ? (
                <p className="text-xs text-gray-500 italic">Sin conversaciones previas.</p>
              ) : (
                <ul className="space-y-2">
                  {historial.map(h => (
                    <li key={h.thread_id} className="text-sm border-l-2 pl-2 py-0.5" style={{ borderColor: h.account_color || '#7c3aed' }}>
                      <p className="font-medium text-gray-900 truncate text-xs">{h.asunto || '(sin asunto)'}</p>
                      <p className="text-xs text-gray-500 flex justify-between gap-2">
                        <span className="truncate">{h.account_label}</span>
                        <span>{h.ultimo_mensaje_at ? new Date(h.ultimo_mensaje_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }) : ''}</span>
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : loadingFicha ? (
          <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin mx-auto text-purple-600" /></div>
        ) : contactoExterno ? (
          <div className="bg-white rounded-lg border p-3 space-y-2" data-testid="ficha-contacto-externo">
            <div className="flex items-center gap-2">
              <UserCircle className="w-8 h-8 text-sky-600" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-gray-900 leading-tight truncate">{contactoExterno.nombre}</p>
                <p className="text-xs text-sky-700">Contacto externo</p>
              </div>
            </div>
            <div className="text-sm space-y-1 pt-1 border-t">
              <p className="truncate"><span className="text-gray-500">Email:</span> <span className="font-medium">{contactoExterno.email}</span></p>
              {contactoExterno.organizacion && <p className="truncate"><span className="text-gray-500">Organización:</span> <span className="font-medium">{contactoExterno.organizacion}</span></p>}
              {contactoExterno.telefono && <p><span className="text-gray-500">Teléfono:</span> <span className="font-medium">{contactoExterno.telefono}</span></p>}
              {contactoExterno.notas && (
                <p className="text-xs text-gray-600 whitespace-pre-wrap pt-1 border-t mt-1">{contactoExterno.notas}</p>
              )}
            </div>
            <Link
              to="/admin/bandeja-unificada/contactos-etiquetas"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-sky-300 text-sky-700 rounded hover:bg-sky-50 w-full justify-center"
              data-testid="btn-editar-contacto-externo"
            >
              <Edit3 className="w-3.5 h-3.5" /> Gestionar contactos
            </Link>
            <div className="pt-2 border-t">
              <p className="text-xs text-gray-500 mb-1">También podés vincular este email a un matriculado:</p>
              <VincularForm conv={conv} onDone={onRefresh} />
            </div>
          </div>
        ) : loadingContacto ? (
          <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin mx-auto text-sky-600" /></div>
        ) : (
          <VincularForm conv={conv} onDone={onRefresh} />
        )}
      </div>
    </aside>
  );
}

// ─── Formulario de vinculación manual ───
function VincularForm({ conv, onDone }) {
  const [q, setQ] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [guardarAlt, setGuardarAlt] = useState(true);
  const [vinculando, setVinculando] = useState(false);
  const [error, setError] = useState('');

  const buscar = async () => {
    if (q.trim().length < 2) { setError('Ingresá al menos 2 caracteres'); return; }
    setBuscando(true); setError('');
    try {
      const r = await axios.get(`${API_URL}/bandeja-unificada/matriculados/buscar?q=${encodeURIComponent(q)}`);
      setResultados(r.data || []);
      if ((r.data || []).length === 0) setError('Sin resultados');
    } catch (e) { setError(e.response?.data?.detail || 'Error'); }
    setBuscando(false);
  };

  const vincular = async () => {
    if (!seleccionado) return;
    setVinculando(true);
    try {
      await axios.post(`${API_URL}/bandeja-unificada/conversations/${encodeURIComponent(conv.thread_id)}/vincular`, {
        trabajador_id: seleccionado.id,
        guardar_email_alternativo: guardarAlt,
      });
      onDone?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al vincular');
    } finally {
      setVinculando(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="form-vincular">
      <div className="bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
        <div>
          <p className="font-semibold text-sm text-amber-900">Usuario no identificado</p>
          <p className="text-xs text-amber-800">El email del remitente no coincide con ningún matriculado.</p>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Buscar por DNI, matrícula o nombre</Label>
        <div className="flex gap-1">
          <Input
            placeholder="DNI, matrícula o nombre…"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscar(); } }}
            className="text-sm"
            data-testid="input-buscar-matriculado"
          />
          <Button variant="outline" size="sm" onClick={buscar} disabled={buscando} data-testid="btn-buscar-matriculado">
            {buscando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {resultados.length > 0 && !seleccionado && (
        <div className="border rounded max-h-52 overflow-y-auto divide-y bg-white" data-testid="lista-matriculados">
          {resultados.map(t => (
            <button
              key={t.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-purple-50"
              onClick={() => setSeleccionado(t)}
              data-testid={`match-${t.id}`}
            >
              <p className="font-medium text-sm">{t.apellido}, {t.nombre}</p>
              <p className="text-xs text-gray-500">Mat. {t.numero_matricula} · DNI {t.documento}</p>
            </button>
          ))}
        </div>
      )}
      {seleccionado && (
        <div className="border rounded p-2 bg-white space-y-2">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-medium text-sm">{seleccionado.apellido}, {seleccionado.nombre}</p>
              <p className="text-xs text-gray-500">Mat. {seleccionado.numero_matricula} · DNI {seleccionado.documento}</p>
            </div>
            <button onClick={() => setSeleccionado(null)} className="text-xs text-gray-500 hover:text-gray-700">Cambiar</button>
          </div>
          <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={guardarAlt}
              onChange={e => setGuardarAlt(e.target.checked)}
              className="mt-0.5"
              data-testid="check-guardar-email-alt"
            />
            <span>Guardar este email como <strong>alternativo</strong> del matriculado para reconocerlo automáticamente la próxima vez.</span>
          </label>
          <Button
            onClick={vincular}
            disabled={vinculando}
            className="w-full bg-purple-600 hover:bg-purple-700 text-sm"
            data-testid="btn-confirmar-vincular"
          >
            {vinculando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}
            Vincular a este matriculado
          </Button>
        </div>
      )}
    </div>
  );
}

